import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/components/AuthProvider";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  PurchaseOrderGroupedDraftLines,
  type PurchaseOrderSavedLineHandlers,
} from "@/components/mrp/PurchaseOrderGroupedDraftLines";
import { PurchaseOrderHeaderFieldLabel } from "@/components/mrp/PurchaseOrderHeaderFieldLabel";
import { DeferredCommitStatusGlyph } from "@/components/mrp/DeferredCommitStatusGlyph";
import { DeferredCommitTextArea } from "@/components/mrp/DeferredCommitTextArea";
import {
  deferredCommitFlashClassName,
  useDeferredCommitFieldFeedback,
} from "@/components/mrp/deferredCommitFieldShared";

import { PurchaseInvoiceDetailLink } from "@/components/hub/HubEntityDetailLink";
import { MegaventoryPurchaseOrderExternalLink } from "@/components/mrp/MegaventoryPurchaseOrderLinks";
import { PurchaseOrderSplitDeliveriesDialog } from "@/components/mrp/PurchaseOrderSplitDeliveriesDialog";
import { PurchaseOrderFinancialDocDialog } from "@/components/mrp/PurchaseOrderFinancialDocDialog";
import { apiFetch } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { buildShippingInboundReceivePath } from "@/lib/hubEntityDetailPaths";
import {
  downloadPurchaseOrderPdf,
  generatePurchaseOrderPdf,
} from "@/lib/purchaseOrderPdfApi";
import {
  createApInvoiceDraft,
  patchApInvoice,
} from "@/pages/accounting/apInvoicesApi";
import {
  FALLBACK_MEGAVENTORY_PAYMENT_TERMS_LABELS,
  mapMegaventoryPaymentTermsToLabel,
  megaventoryEnumerationFromPaymentTermsUserInput,
} from "@/lib/integrator-payment-terms";
import { cn, trimText } from "@/lib/utils";
import { QualityFreshdeskTicketPicker } from "@/pages/quality/QualityFreshdeskTicketPicker";
import { HubPageTitle } from "@/components/HubPageTitle";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { MentionTextarea } from "@/components/MentionTextarea";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { SupplierSearchableSelect } from "@/components/mrp/SupplierSearchableSelect";
import {
  CardTitleWithHelp,
  HoverHintContent,
  HoverHintWithGlyph,
  ReadOnlyHoverLayer,
} from "@/components/ui/title-help";
import {
  MRP_PURCHASE_ORDER_MV_STATUS_HOVER_HINT,
  MRP_PURCHASE_ORDER_FULFILLMENT_HOVER_HINT,
  MRP_PURCHASE_ORDER_RECEIVED_QTY_HOVER_HINT,
  MRP_PURCHASE_ORDER_INVOICED_QTY_HOVER_HINT,
  MRP_PURCHASE_ORDER_GIPI_QTY_HOVER_HINT,
  defaultPurchaseOrderTransferDestinationPublicLocationId,
  defaultPurchaseOrderTransferOriginPublicLocationId,
  hubPurchaseOrderStatusBadgeTone,
  hubPurchaseOrderStatusLabel,
  mrpPurchaseOrderMvStatusLabel,
  purchaseOrderFulfillmentStatusLabel,
  mrpTransferReasonLabel,
  type MrpPurchaseOrder,
  type MrpPurchaseOrderDetailResponse,
  type MrpPurchaseOrderEvent,
  type MrpPurchaseOrderLine,
  type MrpPurchaseOrderLinkedTransferRequest,
  type MrpPurchaseOrderTimeline,
  type MrpSkuHoverHintFields,
  formatNumber,
  formatPoMoney,
  formatPurchaseOrderTimestamp,
  mrpPurchaseOrderIsHubOwned,
  mrpTransferRequestCancelConfirmMessage,
  addPurchaseOrderFreshdeskTicket,
  removePurchaseOrderFreshdeskTicket,
} from "@/lib/mrp";
import {
  PURCHASE_ORDER_AMOUNT_ROUNDING_HOVER_HINT,
  PURCHASE_ORDER_AMOUNT_ROUNDING_MODE_LABELS,
  parsePurchaseOrderAmountRoundingMode,
} from "@/lib/purchaseOrderAmountRounding";
import {
  transferEndpointLocationLabelById,
  useTransferEndpointLocations,
} from "@/lib/transferEndpointLocations";
import {
  operationalLocationFilterOptions,
  publicLocationIdForFilterCode,
} from "@/lib/operationalLocationFilter";
import {
  purchaseOrderMegaventoryChangeKey,
  usePurchaseOrderMegaventoryWriteQueue,
} from "@/lib/usePurchaseOrderMegaventoryWriteQueue";
import {
  buildPurchaseOrderSubmitLines,
  draftComposeOrderTotal,
  draftSupplierDataIdForPurchaseOrder,
  emptyMvLine,
  paymentTermsMvFromSupplier,
  purchaseOrderCurrencyFromSupplier,
  purchaseOrderLinesToDraftForm,
  purchaseOrderToDraftFormHeader,
  sanitizeMvPoLineForms,
  validatePurchaseOrderSubmitLines,
  type MvPoLineForm,
  type PurchaseOrderCurrency,
} from "@/lib/purchaseOrderDraftForm";
import {
  defaultSavedTransferRequestBody,
  purchaseOrderSavedLineCommercialFieldsEditable,
  savedPurchaseOrderLinesToDisplayForm,
} from "@/lib/purchaseOrderSavedLineDisplay";
import {
  HubDraftPersistField,
  HubDraftPersistInput,
  PURCHASE_ORDER_EVENT_TYPE_LABEL,
  PurchaseOrderEventSummaryCell,
  formatPoActor,
  megaventoryLifecycleStatusForHubActions,
  normalizeHubPurchaseOrderStatus,
  normalizePurchaseOrderMvStatus,
  parsePositiveSupplierId,
  purchaseOrderMvStatusBadgeTone,
  purchaseOrderPaymentTermsDiffersFromSupplier,
  type SupplierForCompose,
} from "./purchaseOrderDetailPageModel";
import { DigiKeyOrderModal } from "./DigiKeyOrderModal";
import { purchaseOrderApiOrderingProvider } from "./purchaseOrderApiOrdering";

export function PurchaseOrderDetail() {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const hubWritesAllowed = useHubModuleWrite("mrp");
  const { locations: transferLocations } = useTransferEndpointLocations();
  const hubDraftLocationOptions = useMemo(
    () => operationalLocationFilterOptions(transferLocations),
    [transferLocations],
  );
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const id = idParam != null ? Number.parseInt(idParam, 10) : Number.NaN;
  const [hubDraftSupplierId, setHubDraftSupplierId] = useState<string>("");
  const [hubDraftSupplierSearch, setHubDraftSupplierSearch] = useState("");
  const [hubDraftSupplierLabel, setHubDraftSupplierLabel] = useState<string | null>(null);
  const [hubDraftLocationCode, setHubDraftLocationCode] = useState<string>("sgs");
  const [hubDraftLocationId, setHubDraftLocationId] = useState<number | null>(null);
  const hubDraftLocationSelectOptions = useMemo(() => {
    const options = [...hubDraftLocationOptions];
    if (
      hubDraftLocationId != null &&
      !options.some((option) => option.locationId === hubDraftLocationId)
    ) {
      options.unshift({
        value: hubDraftLocationCode.trim().toLowerCase() || String(hubDraftLocationId),
        label: hubDraftLocationCode.trim() || `Location ${String(hubDraftLocationId)}`,
        locationId: hubDraftLocationId,
      });
    }
    return options;
  }, [hubDraftLocationCode, hubDraftLocationId, hubDraftLocationOptions]);
  const hubDraftSupplierIdNumeric =
    hubDraftSupplierId !== "" ? Number.parseInt(hubDraftSupplierId, 10) : Number.NaN;
  const prevHubDraftSupplierIdRef = useRef<string>("");
  const autosaveTimerRef = useRef<number | null>(null);
  const autosaveInFlightRef = useRef(false);
  const autosavePendingRef = useRef(false);
  const persistHubDraftChainRef = useRef(Promise.resolve());
  const persistHubDraftHeaderRef = useRef<(input: HubDraftPersistInput) => Promise<void>>(async () => {});
  const enqueuePaymentTermsChangeRef = useRef<(payment_terms: string | null) => Promise<void>>(async () => {});
  const lastAutosavePayloadRef = useRef<string | null>(null);
  const autosaveSavedFadeTimerRef = useRef<number | null>(null);
  const createInMegaventoryPendingRef = useRef(false);
  const [draftAutosaveStatus, setDraftAutosaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [splitDialog, setSplitDialog] = useState<{
    purchaseOrderItemId: number;
    cpn: string;
    prefillExpectedArrival: string | null;
  } | null>(null);
  const [financialDocDialogOpen, setFinancialDocDialogOpen] = useState(false);
  const [financialDocSuccess, setFinancialDocSuccess] = useState<{
    mv_document_id?: number;
  } | null>(null);
  const [historyCommentDraft, setHistoryCommentDraft] = useState("");
  const [specialInstructionsJustSaved, setSpecialInstructionsJustSaved] = useState(false);
  const [paymentTermsJustSaved, setPaymentTermsJustSaved] = useState(false);
  const [amountRoundingModeJustSaved, setAmountRoundingModeJustSaved] = useState(false);
  const [hubSupplierSavePending, setHubSupplierSavePending] = useState(false);
  const [hubSupplierJustSaved, setHubSupplierJustSaved] = useState(false);
  const [hubSupplierSaveError, setHubSupplierSaveError] = useState<string | null>(null);
  const [hubLocationSavePending, setHubLocationSavePending] = useState(false);
  const [hubLocationJustSaved, setHubLocationJustSaved] = useState(false);
  const [hubLocationSaveError, setHubLocationSaveError] = useState<string | null>(null);
  const [paymentTermsSaveError, setPaymentTermsSaveError] = useState<string | null>(null);
  const [amountRoundingModeSaveError, setAmountRoundingModeSaveError] = useState<string | null>(null);
  const [specialInstructionsSaveError, setSpecialInstructionsSaveError] = useState<string | null>(null);
  const [draftErrorBanner, setDraftErrorBanner] = useState<string | null>(null);
  const [digikeyOrderOpen, setDigikeyOrderOpen] = useState(false);
  const [draftLines, setDraftLines] = useState<MvPoLineForm[]>([]);
  const [pendingNewLines, setPendingNewLines] = useState<MvPoLineForm[]>([]);
  useEffect(() => {
    setPendingNewLines([]);
  }, [id]);
  const updateDraftLines = useCallback((next: MvPoLineForm[] | ((previous: MvPoLineForm[]) => MvPoLineForm[])) => {
    setDraftLines((previous) => {
      const resolved = typeof next === "function" ? next(previous) : next;
      return sanitizeMvPoLineForms(resolved);
    });
  }, []);
  const [draftCurrency, setDraftCurrency] = useState<PurchaseOrderCurrency>("USD");
  const [draftPaymentTerms, setDraftPaymentTerms] = useState("None");
  const [draftComments, setDraftComments] = useState("");
  const draftFormHydratedIdRef = useRef<number | null>(null);

  const paymentTermLabelsQuery = useQuery({
    queryKey: ["mrp", "megaventory-payment-term-labels"],
    queryFn: () =>
      apiFetch<{
        rows: { sb_enumeration: string; display_label: string; updated_at: string }[];
      }>("/api/mrp/megaventory-payment-term-labels"),
  });

  const enumerationToLabel = useMemo(() => {
    const rows = paymentTermLabelsQuery.data?.rows;
    if (!rows?.length) {
      return FALLBACK_MEGAVENTORY_PAYMENT_TERMS_LABELS;
    }
    const map: Record<string, string> = {};
    for (const row of rows) {
      map[row.sb_enumeration] = row.display_label;
    }
    return map;
  }, [paymentTermLabelsQuery.data?.rows]);

  const poQuery = useQuery({
    queryKey: ["mrp", "purchase-orders", "one", id],
    queryFn: () => apiFetch<MrpPurchaseOrderDetailResponse>(`/api/mrp/purchase-orders/${id}`),
    enabled: Number.isFinite(id),
  });

  const purchaseOrder = poQuery.data?.purchase_order;
  const timeline = poQuery.data?.timeline;
  const events = poQuery.data?.events ?? [];
  const lines = poQuery.data?.lines ?? [];
  const transferRequests = poQuery.data?.transfer_requests ?? [];
  const inboundReceipts = poQuery.data?.inbound_receipts ?? [];
  const accountsPayableInvoices = poQuery.data?.accounts_payable_invoices ?? [];
  const directGipis = poQuery.data?.direct_gipis ?? [];
  const gipiMvNumberByPurchaseInvoiceId = new Map<number, string>();
  for (const invoice of accountsPayableInvoices) {
    const purchaseInvoiceId = Number.parseInt(invoice.purchase_invoice_id ?? "", 10);
    const mvNumber = invoice.purchase_invoice_mv_number?.trim() ?? "";
    if (Number.isFinite(purchaseInvoiceId) && purchaseInvoiceId > 0 && mvNumber !== "") {
      gipiMvNumberByPurchaseInvoiceId.set(purchaseInvoiceId, mvNumber);
    }
  }
  for (const gipi of directGipis) {
    const purchaseInvoiceId = Number.parseInt(gipi.id, 10);
    const mvNumber = gipi.mv_number?.trim() ?? "";
    if (Number.isFinite(purchaseInvoiceId) && purchaseInvoiceId > 0 && mvNumber !== "") {
      gipiMvNumberByPurchaseInvoiceId.set(purchaseInvoiceId, mvNumber);
    }
  }
  const isHubOwned = purchaseOrder != null && mrpPurchaseOrderIsHubOwned(purchaseOrder);
  const isHubOnlyDraft = Boolean(isHubOwned && purchaseOrder?.purchase_order_mv_id == null);
  const isDraftOwner = false;
  const hubStatus = normalizeHubPurchaseOrderStatus(purchaseOrder?.hub_status);
  const draftApInvoice = accountsPayableInvoices.find((invoice) => invoice.status === "draft");
  const showFinalize = false;
  const showCreateInMegaventory = false;
  const showLifecycleActions = true;
  const pageTitle = (() => {
    const poNumber = purchaseOrder?.po_number?.trim();
    if (poNumber) return poNumber;
    if (Number.isFinite(id)) return `PO #${id}`;
    return "Purchase order";
  })();
  const mvStatus = normalizePurchaseOrderMvStatus(purchaseOrder?.mv_status);
  const displayOrderTotal = isDraftOwner
    ? draftComposeOrderTotal(draftLines, parsePurchaseOrderAmountRoundingMode(purchaseOrder?.amount_rounding_mode))
    : (purchaseOrder?.order_total ?? null);
  const displayCurrency = purchaseOrder?.currency ?? draftCurrency;
  const hubCreatedInHubLabel =
    purchaseOrder?.hub_created_at?.trim()
      ? `${formatPurchaseOrderTimestamp(purchaseOrder.hub_created_at)} · ${formatPoActor(timeline?.hub_created_by ?? session?.user?.email)}`
      : null;

  const hubDraftSupplierQuery = useQuery({
    queryKey: ["mrp", "suppliers-for-purchase-order", "hub-draft", hubDraftSupplierIdNumeric],
    queryFn: async () => {
      const response = await apiFetch<{ rows: SupplierForCompose[] }>("/api/mrp/suppliers-for-purchase-order", {
        query: { q: hubDraftSupplierId },
      });
      const row = response.rows.find(
        (supplierRow) => Number(supplierRow.id) === hubDraftSupplierIdNumeric,
      );
      if (row == null) {
        throw new Error("Supplier not found.");
      }
      return row;
    },
    enabled:
      isHubOnlyDraft && Number.isFinite(hubDraftSupplierIdNumeric) && hubDraftSupplierIdNumeric > 0,
  });

  useEffect(() => {
    const order = poQuery.data?.purchase_order;
    const orderLines = poQuery.data?.lines ?? [];
    if (order == null) {
      draftFormHydratedIdRef.current = null;
      return;
    }
    if (draftFormHydratedIdRef.current === order.id) {
      return;
    }
    draftFormHydratedIdRef.current = order.id;
    if (order.is_draft && order.can_edit_draft) {
      const header = purchaseOrderToDraftFormHeader(order);
      setDraftCurrency(header.currency);
      setDraftPaymentTerms(header.paymentTermsEnumeration);
      setDraftComments(header.comments);
      const hydratedLines = purchaseOrderLinesToDraftForm(orderLines, order.location_code);
      updateDraftLines(
        hydratedLines.length > 0
          ? hydratedLines
          : order.purchase_order_mv_id == null
            ? [emptyMvLine(order.location_code)]
            : [],
      );
    }
    if (mrpPurchaseOrderIsHubOwned(order) && order.purchase_order_mv_id == null) {
      setHubDraftLocationCode(order.location_code);
      setHubDraftLocationId(
        order.location_id != null && Number.isFinite(order.location_id) && order.location_id > 0
          ? order.location_id
          : publicLocationIdForFilterCode(order.location_code, hubDraftLocationOptions),
      );
      setHubDraftSupplierId(order.supplier_id != null && order.supplier_id > 0 ? String(order.supplier_id) : "");
      setHubDraftSupplierLabel(trimText(order.supplier) || null);
      prevHubDraftSupplierIdRef.current =
        order.supplier_id != null && order.supplier_id > 0 ? String(order.supplier_id) : "";
    }
  }, [poQuery.data?.purchase_order, poQuery.data?.lines, updateDraftLines]);

  useEffect(() => {
    if (!isHubOnlyDraft || hubDraftSupplierId === "" || hubDraftSupplierQuery.data == null) {
      return;
    }
    if (prevHubDraftSupplierIdRef.current === hubDraftSupplierId) {
      return;
    }
    prevHubDraftSupplierIdRef.current = hubDraftSupplierId;
    const nextCurrency = purchaseOrderCurrencyFromSupplier(hubDraftSupplierQuery.data.currency);
    const nextPaymentTerms = megaventoryEnumerationFromPaymentTermsUserInput(
      paymentTermsMvFromSupplier(hubDraftSupplierQuery.data.payment_terms),
      enumerationToLabel,
    );
    setDraftCurrency(nextCurrency);
    setDraftPaymentTerms(nextPaymentTerms);
    const savedPaymentTerms = (poQuery.data?.purchase_order?.payment_terms ?? "").trim();
    if (hubWritesAllowed && nextPaymentTerms !== savedPaymentTerms) {
      void enqueuePaymentTermsChangeRef.current(nextPaymentTerms).catch(() => undefined);
    }
    if (hubWritesAllowed) {
      void persistHubDraftHeaderRef.current({
        locationCode: hubDraftLocationCode,
        supplierId: parsePositiveSupplierId(hubDraftSupplierId),
        currency: nextCurrency,
        statusFields: ["supplier"],
      }).catch(() => undefined);
    }
    if (hubDraftSupplierLabel == null) {
      setHubDraftSupplierLabel(
        hubDraftSupplierQuery.data.mv_supplier_name ?? `Supplier #${hubDraftSupplierId}`,
      );
    }
  }, [
    enumerationToLabel,
    hubDraftLocationCode,
    hubDraftSupplierId,
    hubDraftSupplierLabel,
    hubDraftSupplierQuery.data,
    hubWritesAllowed,
    isHubOnlyDraft,
    poQuery.data?.purchase_order?.payment_terms,
  ]);

  const draftLineSkuList = useMemo(
    () => draftLines.map((line) => trimText(line.sku)).filter((sku) => sku.length > 0),
    [draftLines],
  );

  const draftSkuHintsQuery = useQuery({
    queryKey: ["mrp", "sku-hover-hints", "po-draft", draftLineSkuList],
    queryFn: () =>
      apiFetch<{ sku_hover_hints: Record<string, MrpSkuHoverHintFields> }>("/api/mrp/sku-hover-hints", {
        query: { cpns: draftLineSkuList.join(",") },
      }),
    enabled: isDraftOwner && draftLineSkuList.length > 0,
    refetchOnWindowFocus: false,
  });

  const cpnList = useMemo(() => lines.map((line) => line.cpn).filter(Boolean), [lines]);

  const savedDisplayLines = useMemo(
    () =>
      purchaseOrder == null
        ? []
        : savedPurchaseOrderLinesToDisplayForm(lines, purchaseOrder.location_code, transferRequests),
    [lines, purchaseOrder, transferRequests],
  );

  const savedLinesWithPendingAdds = useMemo(
    () => [...savedDisplayLines, ...pendingNewLines],
    [pendingNewLines, savedDisplayLines],
  );

  const skuHintsQuery = useQuery({
    queryKey: ["mrp", "sku-hover-hints", cpnList],
    queryFn: () =>
      apiFetch<{ sku_hover_hints: Record<string, MrpSkuHoverHintFields> }>("/api/mrp/sku-hover-hints", {
        query: { cpns: cpnList.join(",") },
      }),
    enabled: cpnList.length > 0 && poQuery.isSuccess,
    refetchOnWindowFocus: false,
  });

  const invalidatePurchaseOrder = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ["mrp", "purchase-orders"] });
    await queryClient.invalidateQueries({ queryKey: ["mrp", "purchase-orders", "one", id] });
    await queryClient.invalidateQueries({ queryKey: ["mrp", "incoming"] });
  };

  const verifyMutation = useMutation({
    mutationFn: async () => {
      const draftSupplierId = parsePositiveSupplierId(hubDraftSupplierId);
      if (isHubOwned && isHubOnlyDraft) {
        await persistHubDraftChainRef.current;
        await persistHubDraftHeaderRef.current({
          locationCode: hubDraftLocationCode,
          supplierId: draftSupplierId,
          statusFields: ["supplier"],
        });
      }
      const persistedSupplierId = purchaseOrder?.supplier_id;
      const hasSupplier =
        parsePositiveSupplierId(persistedSupplierId) != null || draftSupplierId != null;
      if (isHubOwned && !hasSupplier) {
        throw new Error("Select a supplier before approving this purchase order.");
      }
      return apiFetch(`/api/mrp/purchase-orders/${id}/verify`, {
        method: "POST",
        body: draftSupplierId != null ? { supplier_id: draftSupplierId } : {},
      });
    },
    onSuccess: invalidatePurchaseOrder,
  });

  const revertMutation = useMutation({
    mutationFn: () => apiFetch(`/api/mrp/purchase-orders/${id}/revert-to-pending`, { method: "POST" }),
    onSuccess: invalidatePurchaseOrder,
  });

  const cancelMutation = useMutation({
    mutationFn: () => apiFetch(`/api/mrp/purchase-orders/${id}/cancel`, { method: "POST" }),
    onSuccess: invalidatePurchaseOrder,
  });

  const closeMutation = useMutation({
    mutationFn: (reason: string) =>
      apiFetch(`/api/mrp/purchase-orders/${id}/close`, {
        method: "POST",
        body: { reason },
      }),
    onSuccess: invalidatePurchaseOrder,
  });

  const downloadPdfMutation = useMutation({
    mutationFn: () => generatePurchaseOrderPdf(id),
    onSuccess: (pdfBase64) => {
      downloadPurchaseOrderPdf(pdfBase64, purchaseOrder?.po_number ?? null, id);
    },
  });

  const createVendorInvoiceMutation = useMutation({
    mutationFn: async () => {
      const draft = await createApInvoiceDraft();
      const invoice = await patchApInvoice(draft.id, {
        purchase_order_id: id,
        supplier_id: purchaseOrder?.supplier_id ?? null,
      });
      return invoice;
    },
    onSuccess: (invoice) => {
      queryClient.setQueryData(["accounting", "ap-invoice", invoice.id], invoice);
      navigate(`/accounting/ap/${encodeURIComponent(invoice.id)}/edit`);
    },
  });

  const megaventoryWriteQueue = usePurchaseOrderMegaventoryWriteQueue(id);

  const patchPurchaseOrderHeaderInCache = useCallback(
    (
      patch: Partial<
        Pick<MrpPurchaseOrder, "payment_terms" | "amount_rounding_mode" | "special_instructions">
      >,
    ) => {
      queryClient.setQueryData<{
        purchase_order: MrpPurchaseOrder;
        lines: MrpPurchaseOrderLine[];
        events: MrpPurchaseOrderEvent[];
        transfer_requests: MrpPurchaseOrderLinkedTransferRequest[];
        timeline: MrpPurchaseOrderTimeline;
      }>(["mrp", "purchase-orders", "one", id], (current) => {
        if (current == null) {
          return current;
        }
        return {
          ...current,
          purchase_order: { ...current.purchase_order, ...patch },
        };
      });
    },
    [id, queryClient],
  );

  const applyMegaventoryWriteResult = useCallback(
    async (
      result: Awaited<ReturnType<typeof megaventoryWriteQueue.enqueue>>,
      wantedLineDescription?: { purchase_order_item_id: number; description: string | null },
      wantedLineUnitPrice?: { purchase_order_item_id: number; unit_price: number },
      wantedLineQuantity?: { purchase_order_item_id: number; quantity: number },
    ) => {
      if (wantedLineDescription != null && result.line_rows.length > 0) {
        const savedRow = result.line_rows.find(
          (row) => row.purchase_order_item_id === wantedLineDescription.purchase_order_item_id,
        );
        const savedDescription =
          savedRow?.description == null || String(savedRow.description).trim() === ""
            ? null
            : String(savedRow.description).trim();
        const wanted =
          wantedLineDescription.description == null ||
          String(wantedLineDescription.description).trim() === ""
            ? null
            : String(wantedLineDescription.description).trim();
        if (savedDescription !== wanted) {
          throw new Error("The server did not store your description change. Wait a moment and try again.");
        }
        queryClient.setQueryData<{
          purchase_order: MrpPurchaseOrder;
          lines: MrpPurchaseOrderLine[];
          events: MrpPurchaseOrderEvent[];
          transfer_requests: MrpPurchaseOrderLinkedTransferRequest[];
          timeline: MrpPurchaseOrderTimeline;
        }>(["mrp", "purchase-orders", "one", id], (current) => {
          if (current == null) {
            return current;
          }
          return {
            ...current,
            lines: current.lines.map((row) =>
              row.id === wantedLineDescription.purchase_order_item_id
                ? { ...row, description: savedDescription }
                : row,
            ),
          };
        });
      }
      if (wantedLineUnitPrice != null && result.line_rows.length > 0) {
        const savedRow = result.line_rows.find(
          (row) => row.purchase_order_item_id === wantedLineUnitPrice.purchase_order_item_id,
        );
        const savedUnitPrice = savedRow?.unit_price;
        if (savedUnitPrice == null || Math.abs(Number(savedUnitPrice) - wantedLineUnitPrice.unit_price) >= 1e-9) {
          throw new Error("The server did not store your unit price change. Wait a moment and try again.");
        }
        queryClient.setQueryData<{
          purchase_order: MrpPurchaseOrder;
          lines: MrpPurchaseOrderLine[];
          events: MrpPurchaseOrderEvent[];
          transfer_requests: MrpPurchaseOrderLinkedTransferRequest[];
          timeline: MrpPurchaseOrderTimeline;
        }>(["mrp", "purchase-orders", "one", id], (current) => {
          if (current == null) {
            return current;
          }
          return {
            ...current,
            lines: current.lines.map((row) =>
              row.id === wantedLineUnitPrice.purchase_order_item_id
                ? { ...row, unit_price: savedUnitPrice }
                : row,
            ),
          };
        });
      }
      if (wantedLineQuantity != null && result.line_rows.length > 0) {
        const savedRow = result.line_rows.find(
          (row) => row.purchase_order_item_id === wantedLineQuantity.purchase_order_item_id,
        );
        const savedQuantity = savedRow?.quantity;
        if (savedQuantity == null || Math.abs(Number(savedQuantity) - wantedLineQuantity.quantity) >= 1e-9) {
          throw new Error("The server did not store your quantity change. Wait a moment and try again.");
        }
        queryClient.setQueryData<{
          purchase_order: MrpPurchaseOrder;
          lines: MrpPurchaseOrderLine[];
          events: MrpPurchaseOrderEvent[];
          transfer_requests: MrpPurchaseOrderLinkedTransferRequest[];
          timeline: MrpPurchaseOrderTimeline;
        }>(["mrp", "purchase-orders", "one", id], (current) => {
          if (current == null) {
            return current;
          }
          return {
            ...current,
            lines: current.lines.map((row) =>
              row.id === wantedLineQuantity.purchase_order_item_id
                ? { ...row, quantity_ordered: Number(savedQuantity) }
                : row,
            ),
          };
        });
      }
    },
    [id, queryClient],
  );

  const addHistoryCommentMutation = useMutation({
    mutationFn: (comment: string) =>
      apiFetch<{ ok: boolean; events: MrpPurchaseOrderEvent[] }>(`/api/mrp/purchase-orders/${id}/comments`, {
        method: "POST",
        body: { comment },
      }),
    onSuccess: async () => {
      setHistoryCommentDraft("");
      await invalidatePurchaseOrder();
    },
  });

  useEffect(() => {
    if (!paymentTermsJustSaved) return;
    const timer = window.setTimeout(() => setPaymentTermsJustSaved(false), 2500);
    return () => window.clearTimeout(timer);
  }, [paymentTermsJustSaved]);

  useEffect(() => {
    if (!amountRoundingModeJustSaved) return;
    const timer = window.setTimeout(() => setAmountRoundingModeJustSaved(false), 2500);
    return () => window.clearTimeout(timer);
  }, [amountRoundingModeJustSaved]);

  useEffect(() => {
    if (!specialInstructionsJustSaved) return;
    const timer = window.setTimeout(() => setSpecialInstructionsJustSaved(false), 2500);
    return () => window.clearTimeout(timer);
  }, [specialInstructionsJustSaved]);

  useEffect(() => {
    if (!hubSupplierJustSaved) return;
    const timer = window.setTimeout(() => setHubSupplierJustSaved(false), 2500);
    return () => window.clearTimeout(timer);
  }, [hubSupplierJustSaved]);

  useEffect(() => {
    if (!hubLocationJustSaved) return;
    const timer = window.setTimeout(() => setHubLocationJustSaved(false), 2500);
    return () => window.clearTimeout(timer);
  }, [hubLocationJustSaved]);

  const paymentTermsFlashTone = useDeferredCommitFieldFeedback(paymentTermsJustSaved, paymentTermsSaveError);
  const amountRoundingModeFlashTone = useDeferredCommitFieldFeedback(
    amountRoundingModeJustSaved,
    amountRoundingModeSaveError,
  );
  const hubSupplierFlashTone = useDeferredCommitFieldFeedback(hubSupplierJustSaved, hubSupplierSaveError);
  const hubLocationFlashTone = useDeferredCommitFieldFeedback(hubLocationJustSaved, hubLocationSaveError);

  const enqueuePaymentTermsChange = useCallback(
    async (payment_terms: string | null) => {
      setPaymentTermsSaveError(null);
      try {
        const result = await megaventoryWriteQueue.enqueue({ type: "payment_terms", payment_terms });
        if (result.changed) {
          setPaymentTermsJustSaved(true);
        }
        await applyMegaventoryWriteResult(result);
        patchPurchaseOrderHeaderInCache({ payment_terms });
      } catch (error) {
        setPaymentTermsSaveError(error instanceof Error ? error.message : "Could not save payment terms.");
        throw error;
      }
    },
    [applyMegaventoryWriteResult, megaventoryWriteQueue, patchPurchaseOrderHeaderInCache],
  );

  enqueuePaymentTermsChangeRef.current = enqueuePaymentTermsChange;

  const enqueueAmountRoundingModeChange = useCallback(
    async (amount_rounding_mode: string) => {
      setAmountRoundingModeSaveError(null);
      try {
        const result = await megaventoryWriteQueue.enqueue({ type: "amount_rounding_mode", amount_rounding_mode });
        if (result.changed) {
          setAmountRoundingModeJustSaved(true);
        }
        await applyMegaventoryWriteResult(result);
        patchPurchaseOrderHeaderInCache({
          amount_rounding_mode: parsePurchaseOrderAmountRoundingMode(amount_rounding_mode),
        });
      } catch (error) {
        setAmountRoundingModeSaveError(error instanceof Error ? error.message : "Could not save amount rounding.");
        throw error;
      }
    },
    [applyMegaventoryWriteResult, megaventoryWriteQueue, patchPurchaseOrderHeaderInCache],
  );

  const enqueueSpecialInstructionsChange = useCallback(
    async (special_instructions: string | null) => {
      setSpecialInstructionsSaveError(null);
      try {
        const result = await megaventoryWriteQueue.enqueue({
          type: "special_instructions",
          special_instructions,
        });
        if (result.changed) {
          setSpecialInstructionsJustSaved(true);
        }
        await applyMegaventoryWriteResult(result);
        patchPurchaseOrderHeaderInCache({ special_instructions });
      } catch (error) {
        setSpecialInstructionsSaveError(
          error instanceof Error ? error.message : "Could not save special instructions.",
        );
        throw error;
      }
    },
    [applyMegaventoryWriteResult, megaventoryWriteQueue, patchPurchaseOrderHeaderInCache],
  );

  const enqueueLineDescriptionChange = useCallback(
    async (purchase_order_item_id: number, description: string | null) => {
      const result = await megaventoryWriteQueue.enqueue({
        type: "line_description",
        purchase_order_item_id,
        description,
      });
      await applyMegaventoryWriteResult(result, { purchase_order_item_id, description });
      return result;
    },
    [applyMegaventoryWriteResult, megaventoryWriteQueue],
  );

  const enqueueLineUnitPriceChange = useCallback(
    async (purchase_order_item_id: number, unit_price: number) => {
      const result = await megaventoryWriteQueue.enqueue({
        type: "line_unit_price",
        purchase_order_item_id,
        unit_price,
      });
      await applyMegaventoryWriteResult(result, undefined, { purchase_order_item_id, unit_price });
      return result;
    },
    [applyMegaventoryWriteResult, megaventoryWriteQueue],
  );

  const enqueueLineQuantityChange = useCallback(
    async (purchase_order_item_id: number, quantity: number) => {
      const result = await megaventoryWriteQueue.enqueue({
        type: "line_quantity",
        purchase_order_item_id,
        quantity,
      });
      await applyMegaventoryWriteResult(result, undefined, undefined, { purchase_order_item_id, quantity });
      return result;
    },
    [applyMegaventoryWriteResult, megaventoryWriteQueue],
  );

  const splitDeliveriesMutation = useMutation({
    mutationFn: async (input: {
      purchaseOrderItemId: number;
      segments: { quantity: number; expected_arrival: string | null }[];
    }) => {
      if (input.segments.length === 1) {
        const only = input.segments[0]!;
        await apiFetch<{ ok: boolean }>(`/api/mrp/mv-purchase-order-items/${input.purchaseOrderItemId}/deliveries`, {
          method: "PUT",
          body: { segments: [] },
        });
        await apiFetch(`/api/mrp/mv-purchase-order-items/${input.purchaseOrderItemId}`, {
          method: "PATCH",
          body: { expected_date: only.expected_arrival },
        });
        return { ok: true as const };
      }
      return apiFetch<{ ok: boolean }>(`/api/mrp/mv-purchase-order-items/${input.purchaseOrderItemId}/deliveries`, {
        method: "PUT",
        body: { segments: input.segments },
      });
    },
    onSuccess: async () => {
      setSplitDialog(null);
      await invalidatePurchaseOrder();
    },
  });

  const savedLineHandlers = useMemo((): PurchaseOrderSavedLineHandlers | undefined => {
    if (isDraftOwner || purchaseOrder == null) {
      return undefined;
    }
    const savedMvStatus = isHubOwned
      ? megaventoryLifecycleStatusForHubActions(hubStatus)
      : normalizePurchaseOrderMvStatus(purchaseOrder.mv_status);
    const canCreateTransferRequest =
      hubWritesAllowed &&
      savedMvStatus !== "cancelled" &&
      savedMvStatus !== "canceled" &&
      savedMvStatus !== "closed";
    const canEditLineCommercialFields = purchaseOrderSavedLineCommercialFieldsEditable({
      hubWritesAllowed,
      isHubOwned,
      hubStatus,
      savedMvStatus,
    });
    const canEditLineQuantity =
      hubWritesAllowed &&
      isHubOwned &&
      hubStatus !== "cancelled" &&
      hubStatus !== "closed";
    const canSplitCommercialLine = canEditLineQuantity;
    const canAddRemoveLines = canEditLineQuantity;
    return {
      purchaseOrderId: purchaseOrder.id,
      canCreateTransferRequest,
      canEditLineDescriptions: isHubOwned
        ? canEditLineCommercialFields
        : savedMvStatus === "pending",
      canEditLineUnitPrice: canEditLineCommercialFields,
      canEditLineQuantity,
      canSplitCommercialLine,
      canAddRemoveLines,
      lineQuantityLockedHoverHint: isHubOwned
        ? "Cancelled or closed purchase orders cannot be edited."
        : "Line quantities on Megaventory-owned orders can be edited only while the order is Pending. Revert to pending to unlock. Purchase invoices block revert.",
      lineDescriptionLockedHoverHint: isHubOwned
        ? "Cancelled or closed purchase orders cannot be edited."
        : "Line descriptions can be edited only while the order is Pending. Revert to pending to unlock.",
      lineUnitPriceLockedHoverHint: isHubOwned
        ? "Cancelled or closed purchase orders cannot be edited."
        : "Unit prices can be edited on Pending or Approved purchase orders. On Approved orders the hub reverts, saves, and re-approves in Megaventory automatically.",
      lineRemoveLockedHoverHint: "Cannot remove a line that has already been received.",
      onCommitLineDescription: async (purchaseOrderItemId, description) => {
        await enqueueLineDescriptionChange(purchaseOrderItemId, description);
      },
      onCommitLineUnitPrice: async (purchaseOrderItemId, unitPrice) => {
        await enqueueLineUnitPriceChange(purchaseOrderItemId, unitPrice);
      },
      onCommitLineQuantity: async (purchaseOrderItemId, quantity) => {
        await enqueueLineQuantityChange(purchaseOrderItemId, quantity);
      },
      onSplitCommercialLine: async (purchaseOrderItemId, quantity) => {
        await apiFetch(`/api/mrp/purchase-orders/${purchaseOrder.id}/split-line`, {
          method: "POST",
          body: {
            purchase_order_item_id: purchaseOrderItemId,
            quantity,
          },
        });
        await invalidatePurchaseOrder();
      },
      onCommitNewLine: async (line) => {
        const quantityText = trimText(line.quantity);
        const unitPriceText = trimText(line.unit_price);
        const quantityParsed = Number.parseFloat(quantityText);
        const unitPriceParsed = Number.parseFloat(unitPriceText);
        const quantityPayload =
          quantityText === ""
            ? null
            : Number.isFinite(quantityParsed) && quantityParsed > 0
              ? quantityParsed
              : null;
        const unitPricePayload =
          unitPriceText === ""
            ? null
            : Number.isFinite(unitPriceParsed)
              ? unitPriceParsed
              : null;
        const created = await apiFetch<{ ok: true; purchase_order_item_id: number }>(
          `/api/mrp/purchase-orders/${purchaseOrder.id}/lines`,
          {
            method: "POST",
            body: {
              sku: line.sku,
              quantity: quantityPayload,
              unit_price: unitPricePayload,
              line_description: line.line_description.trim() === "" ? null : line.line_description,
              expected_delivery_date:
                line.expected_delivery_date.trim() === "" ? null : line.expected_delivery_date,
            },
          },
        );
        setPendingNewLines((previous) => previous.filter((row) => row.client_id !== line.client_id));
        const expectedArrival =
          line.expected_delivery_date.trim() === "" ? null : line.expected_delivery_date.trim();
        const lineDescription =
          line.line_description.trim() === "" ? null : line.line_description.trim();
        queryClient.setQueryData<MrpPurchaseOrderDetailResponse>(
          ["mrp", "purchase-orders", "one", id],
          (current) => {
            if (current == null) {
              return current;
            }
            const template = current.lines[0];
            const quantityOrdered =
              quantityPayload != null && Number.isFinite(quantityPayload) ? quantityPayload : 0;
            const newLine: MrpPurchaseOrderLine = {
              id: created.purchase_order_item_id,
              purchase_order_id: purchaseOrder.id,
              purchase_order_mv_id: purchaseOrder.purchase_order_mv_id,
              po_number: purchaseOrder.po_number,
              supplier: purchaseOrder.supplier,
              cpn: trimText(line.sku),
              description: lineDescription,
              sku_catalog_description: template?.sku_catalog_description ?? null,
              mv_unit: template?.mv_unit ?? null,
              location_code: purchaseOrder.location_code,
              quantity_ordered: quantityOrdered,
              unit_price: unitPricePayload,
              quantity_received: 0,
              quantity_invoiced: 0,
              quantity_invoiced_total: 0,
              allocated_stock: 0,
              expected_arrival: expectedArrival,
              status: "open",
              notes: null,
              updated_at: new Date().toISOString(),
              updated_by: null,
              mv_row_detail_id: null,
              delivery_segments: [],
            };
            return { ...current, lines: [...current.lines, newLine] };
          },
        );
      },
      onRemoveLine: async (purchaseOrderItemId) => {
        await apiFetch(`/api/mrp/purchase-orders/${purchaseOrder.id}/lines/${purchaseOrderItemId}`, {
          method: "DELETE",
        });
        queryClient.setQueryData<MrpPurchaseOrderDetailResponse>(
          ["mrp", "purchase-orders", "one", id],
          (current) => {
            if (current == null) {
              return current;
            }
            return {
              ...current,
              lines: current.lines.filter((row) => row.id !== purchaseOrderItemId),
            };
          },
        );
      },
      descriptionSavePendingForItemId: (purchaseOrderItemId) =>
        megaventoryWriteQueue.isQueuedOrSaving(
          purchaseOrderMegaventoryChangeKey({
            type: "line_description",
            purchase_order_item_id: purchaseOrderItemId,
            description: null,
          }),
        ),
      unitPriceSavePendingForItemId: (purchaseOrderItemId) =>
        megaventoryWriteQueue.isQueuedOrSaving(
          purchaseOrderMegaventoryChangeKey({
            type: "line_unit_price",
            purchase_order_item_id: purchaseOrderItemId,
            unit_price: 0,
          }),
        ),
      quantitySavePendingForItemId: (purchaseOrderItemId) =>
        megaventoryWriteQueue.isQueuedOrSaving(
          purchaseOrderMegaventoryChangeKey({
            type: "line_quantity",
            purchase_order_item_id: purchaseOrderItemId,
            quantity: 0,
          }),
        ),
      onOpenSplitDeliveries: (purchaseOrderItemId, cpn, prefillExpectedArrival) => {
        setSplitDialog({ purchaseOrderItemId, cpn, prefillExpectedArrival });
      },
      onAddTransferRequest: async (purchaseOrderItemId, sku, openQuantity, targetDate) => {
        await apiFetch(`/api/mrp/purchase-orders/${purchaseOrder.id}/transfer-requests`, {
          method: "POST",
          body: defaultSavedTransferRequestBody(
            sku,
            purchaseOrder.location_code,
            openQuantity,
            targetDate,
            purchaseOrderItemId,
          ),
        });
        await invalidatePurchaseOrder();
      },
      onUpdateTransferRequest: async (transferRequestId, patch) => {
        await apiFetch(`/api/mrp/transfer-requests/${transferRequestId}`, {
          method: "PATCH",
          body: {
            ...(patch.quantity != null ? { quantity: Number.parseInt(patch.quantity, 10) } : {}),
            ...(patch.destination_location_id != null
              ? { destination_location_id: Number.parseInt(patch.destination_location_id, 10) }
              : {}),
            ...(patch.target_date != null ? { target_date: patch.target_date } : {}),
            ...(patch.notes != null ? { notes: patch.notes.trim() === "" ? null : patch.notes } : {}),
          },
        });
        await invalidatePurchaseOrder();
      },
      onRemoveTransferRequest: async (transferRequestId) => {
        const linked = transferRequests.find((row) => row.id === transferRequestId);
        const remainder = linked != null && linked.remaining_quantity < linked.requested_quantity;
        if (
          !window.confirm(
            mrpTransferRequestCancelConfirmMessage({
              sku: linked?.sku_id ?? "this SKU",
              remainingQuantity: linked?.remaining_quantity ?? 0,
              hasAllocation: remainder,
            }),
          )
        ) {
          return;
        }
        await apiFetch<{ cancelled: boolean }>(`/api/mrp/transfer-requests/${transferRequestId}`, {
          method: "DELETE",
        });
        await invalidatePurchaseOrder();
      },
    };
  }, [
    enqueueLineDescriptionChange,
    enqueueLineUnitPriceChange,
    enqueueLineQuantityChange,
    hubWritesAllowed,
    hubStatus,
    id,
    invalidatePurchaseOrder,
    isDraftOwner,
    isHubOwned,
    megaventoryWriteQueue,
    purchaseOrder,
    queryClient,
    transferRequests,
  ]);

  const buildHubOnlyDraftAutosaveBody = useCallback(
    (input?: {
      locationCode: string;
      locationId?: number | null;
      supplierId: number | null;
      currency?: PurchaseOrderCurrency | null;
    }) => {
      const locationCode = input?.locationCode ?? hubDraftLocationCode;
      const locationId = input?.locationId !== undefined ? input.locationId : hubDraftLocationId;
      const supplierId =
        input?.supplierId !== undefined ? input.supplierId : parsePositiveSupplierId(hubDraftSupplierId);
      return {
        ...(locationId != null && locationId > 0
          ? { location_id: locationId }
          : { location_code: locationCode }),
        ...(supplierId != null ? { supplier_id: supplierId } : {}),
        ...(input?.currency != null ? { currency: input.currency } : {}),
      };
    },
    [hubDraftLocationCode, hubDraftLocationId, hubDraftSupplierId],
  );

  const buildMvDraftAutosaveBody = useCallback(() => {
    if (!purchaseOrder) {
      return null;
    }
    const supplierDataId = draftSupplierDataIdForPurchaseOrder(purchaseOrder);
    if (supplierDataId == null || !Number.isFinite(supplierDataId) || supplierDataId <= 0) {
      return null;
    }
    const bodyLines = buildPurchaseOrderSubmitLines(draftLines, purchaseOrder.location_code);
    if (bodyLines.length === 0) {
      return null;
    }
    const paymentTermsForMegaventory = megaventoryEnumerationFromPaymentTermsUserInput(
      draftPaymentTerms,
      enumerationToLabel,
    );
    return {
      supplier_id: supplierDataId,
      location_code: purchaseOrder.location_code,
      lines: bodyLines,
      currency: displayCurrency,
      payment_terms: paymentTermsForMegaventory,
      comments: draftComments.trim() || null,
    };
  }, [
    displayCurrency,
    draftComments,
    draftLines,
    draftPaymentTerms,
    enumerationToLabel,
    purchaseOrder,
  ]);

  const markDraftAutosaveSaved = useCallback(() => {
    if (autosaveSavedFadeTimerRef.current != null) {
      window.clearTimeout(autosaveSavedFadeTimerRef.current);
    }
    setDraftAutosaveStatus("saved");
    autosaveSavedFadeTimerRef.current = window.setTimeout(() => {
      autosaveSavedFadeTimerRef.current = null;
      setDraftAutosaveStatus("idle");
    }, 2000);
  }, []);

  const persistHubDraftHeader = useCallback(
    async (input: HubDraftPersistInput): Promise<void> => {
      if (!hubWritesAllowed || !isHubOnlyDraft || createInMegaventoryPendingRef.current) {
        return;
      }
      const statusFields =
        input.statusFields.length > 0
          ? input.statusFields
          : (["supplier", "location"] as HubDraftPersistField[]);
      for (const field of statusFields) {
        if (field === "supplier") {
          setHubSupplierSavePending(true);
          setHubSupplierJustSaved(false);
          setHubSupplierSaveError(null);
        } else {
          setHubLocationSavePending(true);
          setHubLocationJustSaved(false);
          setHubLocationSaveError(null);
        }
      }
      const body = buildHubOnlyDraftAutosaveBody(input);
      const payloadKey = JSON.stringify(body);
      if (payloadKey === lastAutosavePayloadRef.current) {
        for (const field of statusFields) {
          if (field === "supplier") {
            setHubSupplierSavePending(false);
            setHubSupplierJustSaved(true);
          } else {
            setHubLocationSavePending(false);
            setHubLocationJustSaved(true);
          }
        }
        return;
      }
      if (autosaveSavedFadeTimerRef.current != null) {
        window.clearTimeout(autosaveSavedFadeTimerRef.current);
        autosaveSavedFadeTimerRef.current = null;
      }
      setDraftAutosaveStatus("saving");
      try {
        await apiFetch(`/api/mrp/purchase-orders/${id}`, {
          method: "PATCH",
          body,
        });
        lastAutosavePayloadRef.current = payloadKey;
        queryClient.setQueryData<MrpPurchaseOrderDetailResponse>(
          ["mrp", "purchase-orders", "one", id],
          (current) => {
            if (current == null) {
              return current;
            }
            const nextOrder = { ...current.purchase_order };
            if (body.supplier_id != null) {
              nextOrder.supplier_id = body.supplier_id;
              if (hubDraftSupplierLabel != null && hubDraftSupplierLabel.trim() !== "") {
                nextOrder.supplier = hubDraftSupplierLabel;
              }
            }
            if ("location_id" in body && body.location_id != null) {
              nextOrder.location_id = body.location_id;
            }
            if ("location_code" in body && body.location_code != null) {
              nextOrder.location_code = body.location_code;
            }
            if (body.currency != null) {
              nextOrder.currency = body.currency;
            }
            return { ...current, purchase_order: nextOrder };
          },
        );
        markDraftAutosaveSaved();
        for (const field of statusFields) {
          if (field === "supplier") {
            setHubSupplierSavePending(false);
            setHubSupplierJustSaved(true);
          } else {
            setHubLocationSavePending(false);
            setHubLocationJustSaved(true);
          }
        }
      } catch {
        lastAutosavePayloadRef.current = null;
        setDraftAutosaveStatus("error");
        const message = "Could not save this purchase order field. Try again.";
        for (const field of statusFields) {
          if (field === "supplier") {
            setHubSupplierSavePending(false);
            setHubSupplierSaveError(message);
          } else {
            setHubLocationSavePending(false);
            setHubLocationSaveError(message);
          }
        }
        throw new Error(message);
      }
    },
    [
      buildHubOnlyDraftAutosaveBody,
      hubWritesAllowed,
      hubDraftSupplierLabel,
      id,
      isHubOnlyDraft,
      markDraftAutosaveSaved,
      queryClient,
    ],
  );

  persistHubDraftHeaderRef.current = persistHubDraftHeader;

  const enqueueHubDraftPersist = useCallback(
    (input: HubDraftPersistInput): Promise<void> => {
      const next = persistHubDraftChainRef.current.then(
        () => persistHubDraftHeader(input),
        () => persistHubDraftHeader(input),
      );
      persistHubDraftChainRef.current = next.then(
        () => undefined,
        () => undefined,
      );
      return next;
    },
    [persistHubDraftHeader],
  );

  const flushDraftAutosave = useCallback(async (): Promise<void> => {
    if (!hubWritesAllowed || createInMegaventoryPendingRef.current) {
      return;
    }
    if (isHubOnlyDraft) {
      try {
        await enqueueHubDraftPersist({
          locationCode: hubDraftLocationCode,
          supplierId: parsePositiveSupplierId(hubDraftSupplierId),
          statusFields: ["supplier", "location"],
        });
      } catch {
        return;
      }
      return;
    }
    if (autosaveInFlightRef.current) {
      autosavePendingRef.current = true;
      return;
    }

    let payloadKey: string;
    try {
      const body = buildMvDraftAutosaveBody();
      if (body == null) {
        return;
      }
      payloadKey = JSON.stringify(body);
    } catch {
      return;
    }

    if (payloadKey === lastAutosavePayloadRef.current) {
      return;
    }

    autosaveInFlightRef.current = true;
    if (autosaveSavedFadeTimerRef.current != null) {
      window.clearTimeout(autosaveSavedFadeTimerRef.current);
      autosaveSavedFadeTimerRef.current = null;
    }
    setDraftAutosaveStatus("saving");
    try {
      await apiFetch("/api/mrp/purchase-order-drafts/save", {
        method: "POST",
        body: JSON.parse(payloadKey),
      });
      await invalidatePurchaseOrder();
      lastAutosavePayloadRef.current = payloadKey;
      markDraftAutosaveSaved();
    } catch {
      setDraftAutosaveStatus("error");
    } finally {
      autosaveInFlightRef.current = false;
      if (autosavePendingRef.current) {
        autosavePendingRef.current = false;
        void flushDraftAutosave();
      }
    }
  }, [
    buildMvDraftAutosaveBody,
    enqueueHubDraftPersist,
    hubDraftLocationCode,
    hubDraftSupplierId,
    hubWritesAllowed,
    id,
    invalidatePurchaseOrder,
    isHubOnlyDraft,
    markDraftAutosaveSaved,
  ]);

  const handleDraftFieldBlur = useCallback(() => {
    if (!hubWritesAllowed || !isHubOnlyDraft) {
      return;
    }
    if (autosaveTimerRef.current != null) {
      window.clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    void flushDraftAutosave();
  }, [flushDraftAutosave, hubWritesAllowed, isHubOnlyDraft]);

  const scheduleDraftAutosaveAfterSelectChange = useCallback(() => {
    if (!hubWritesAllowed || !isHubOnlyDraft) {
      return;
    }
    if (autosaveTimerRef.current != null) {
      window.clearTimeout(autosaveTimerRef.current);
    }
    autosaveTimerRef.current = window.setTimeout(() => {
      autosaveTimerRef.current = null;
      void flushDraftAutosave();
    }, 400);
  }, [flushDraftAutosave, hubWritesAllowed, isHubOnlyDraft]);

  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current != null) {
        window.clearTimeout(autosaveTimerRef.current);
      }
      if (autosaveSavedFadeTimerRef.current != null) {
        window.clearTimeout(autosaveSavedFadeTimerRef.current);
      }
    };
  }, []);

  const createInMegaventoryMutation = useMutation({
    mutationFn: async (
      body:
        | {
            supplier_id: number;
            location_code: string;
            lines: ReturnType<typeof buildPurchaseOrderSubmitLines>;
            currency: string | null;
            payment_terms: string | null;
            comments: string | null;
          }
        | undefined,
    ) => {
      createInMegaventoryPendingRef.current = true;
      try {
        if (showFinalize) {
          return apiFetch<{ purchase_order_id: number; number: string }>(
            `/api/mrp/purchase-orders/${id}/finalize`,
            { method: "POST" },
          );
        }
        if (showCreateInMegaventory) {
          return apiFetch<{
            public_purchase_order_id: number | null;
            erp_sync: { ok: true; summary: unknown } | { ok: false; error: string } | unknown;
          }>(`/api/mrp/purchase-orders/${id}/create-in-megaventory`, { method: "POST" });
        }
        if (body == null) {
          throw new Error("Missing purchase order draft body.");
        }
        await apiFetch<{ ok: true }>("/api/mrp/purchase-order-drafts/save", {
          method: "POST",
          body,
        });
        return apiFetch<{
          public_purchase_order_id: number;
          erp_sync: { ok: true; summary: unknown } | { ok: false; error: string };
        }>("/api/mrp/purchase-order-drafts/finalize", { method: "POST", body });
      } finally {
        createInMegaventoryPendingRef.current = false;
      }
    },
    onSuccess: async (data) => {
      setDraftErrorBanner(null);
      draftFormHydratedIdRef.current = null;
      await invalidatePurchaseOrder();
      const erpSync =
        data != null && typeof data === "object" && "erp_sync" in data
          ? (data as { erp_sync?: { ok: boolean; error?: string } }).erp_sync
          : undefined;
      if (
        erpSync != null &&
        typeof erpSync === "object" &&
        "ok" in erpSync &&
        erpSync.ok === false &&
        "error" in erpSync
      ) {
        setDraftErrorBanner(
          `The purchase order was created in Megaventory, but a later step did not complete: ${String(erpSync.error)}.`,
        );
      }
    },
    onError: (error: Error) => {
      setDraftErrorBanner(error.message);
    },
  });

  const deleteDraftMutation = useMutation({
    mutationFn: async () => {
      if (isHubOnlyDraft) {
        await apiFetch<{ ok: true }>(`/api/mrp/purchase-orders/${id}`, { method: "DELETE" });
        return;
      }
      await apiFetch<{ ok: true }>(`/api/mrp/purchase-orders/${id}/release-hub-draft`, {
        method: "POST",
      });
    },
    onSuccess: async (_data, _vars, _ctx) => {
      setDraftErrorBanner(null);
      draftFormHydratedIdRef.current = null;
      lastAutosavePayloadRef.current = null;
      if (autosaveTimerRef.current != null) {
        window.clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
      await queryClient.invalidateQueries({ queryKey: ["mrp", "purchase-orders"] });
      if (isHubOnlyDraft) {
        navigate("/mrp/purchase-orders", { replace: true });
      } else {
        await invalidatePurchaseOrder();
      }
    },
    onError: (error: Error) => {
      setDraftErrorBanner(error.message);
    },
  });

  const submitDraftBody = () => {
    if (!purchaseOrder) return null;
    const supplierDataId = draftSupplierDataIdForPurchaseOrder(purchaseOrder);
    if (supplierDataId == null || !Number.isFinite(supplierDataId) || supplierDataId <= 0) {
      setDraftErrorBanner("Select a supplier before creating in Megaventory.");
      return null;
    }
    const bodyLines = buildPurchaseOrderSubmitLines(draftLines, purchaseOrder.location_code);
    const validationError = validatePurchaseOrderSubmitLines(bodyLines);
    if (validationError) {
      setDraftErrorBanner(validationError);
      return null;
    }
    setDraftErrorBanner(null);
    const paymentTermsForMegaventory = megaventoryEnumerationFromPaymentTermsUserInput(
      draftPaymentTerms,
      enumerationToLabel,
    );
    setDraftPaymentTerms(paymentTermsForMegaventory);
    return {
      supplier_id: supplierDataId,
      location_code: purchaseOrder.location_code,
      lines: bodyLines,
      currency: displayCurrency,
      payment_terms: paymentTermsForMegaventory,
      comments: draftComments.trim() || null,
    };
  };

  if (!Number.isFinite(id)) {
    return (
      <div className="text-sm text-slate-600">
        <p>Invalid purchase order.</p>
        <Link to="/mrp/purchase-orders" className="text-brand-700 hover:underline">
          Back to list
        </Link>
      </div>
    );
  }

  if (poQuery.isPending) {
    return <p className="text-sm text-slate-600">Loading…</p>;
  }

  if (poQuery.isError || !purchaseOrder) {
    return (
      <div className="text-sm text-rose-700">
        <p>{(poQuery.error as Error | undefined)?.message ?? "Not found."}</p>
        <Link to="/mrp/purchase-orders" className="text-brand-700 hover:underline">
          Back to list
        </Link>
      </div>
    );
  }

  const canApprove = isHubOwned ? hubStatus === "draft" : mvStatus === "pending";
  const apiOrderingProvider = isHubOwned
    ? purchaseOrderApiOrderingProvider(purchaseOrder.supplier)
    : null;
  const hasLinkedSupplier =
    (purchaseOrder.supplier_id != null &&
      Number.isFinite(purchaseOrder.supplier_id) &&
      purchaseOrder.supplier_id > 0) ||
    (Number.isFinite(hubDraftSupplierIdNumeric) && hubDraftSupplierIdNumeric > 0);
  const canRevertToPending = isHubOwned ? hubStatus === "ordered" : mvStatus === "verified";
  const canCancel = isHubOwned
    ? hubStatus === "draft" || hubStatus === "ordered"
    : mvStatus !== "cancelled" && mvStatus !== "closed" && mvStatus !== "fullyinvoiced";
  const canClose = isHubOwned && (hubStatus === "draft" || hubStatus === "ordered");
  const purchaseOrderCancelled = isHubOwned
    ? hubStatus === "cancelled"
    : mvStatus === "cancelled" || mvStatus === "canceled";
  const canEditHeaderFields =
    hubWritesAllowed &&
    (isHubOwned
      ? hubStatus !== "cancelled" && hubStatus !== "closed"
      : mvStatus !== "cancelled" &&
        mvStatus !== "canceled" &&
        mvStatus !== "closed");
  const paymentTermsDiffersFromSupplier = purchaseOrderPaymentTermsDiffersFromSupplier(
    purchaseOrder.payment_terms,
    purchaseOrder.supplier_payment_terms,
    enumerationToLabel,
  );
  const paymentTermsCurrent = isDraftOwner
    ? draftPaymentTerms
    : purchaseOrder.payment_terms?.trim() || "None";
  const paymentTermsDiffersFromSupplierDraft =
    isDraftOwner &&
    purchaseOrderPaymentTermsDiffersFromSupplier(
      draftPaymentTerms,
      purchaseOrder.supplier_payment_terms,
      enumerationToLabel,
    );
  const paymentTermsSelectRows = (() => {
    const pairs = Object.entries(enumerationToLabel).map(([mvEnumeration, displayLabel]) => ({
      mvEnumeration,
      displayLabel,
    }));
    pairs.sort((first, second) =>
      first.displayLabel.localeCompare(second.displayLabel, undefined, { sensitivity: "base" }),
    );
    if (paymentTermsCurrent !== "" && !Object.prototype.hasOwnProperty.call(enumerationToLabel, paymentTermsCurrent)) {
      pairs.unshift({
        mvEnumeration: paymentTermsCurrent,
        displayLabel:
          mapMegaventoryPaymentTermsToLabel(paymentTermsCurrent, enumerationToLabel) ?? paymentTermsCurrent,
      });
    }
    return pairs;
  })();
  const paymentTermsSavePending = megaventoryWriteQueue.isQueuedOrSaving(
    purchaseOrderMegaventoryChangeKey({ type: "payment_terms", payment_terms: null }),
  );
  const amountRoundingModeSavePending = megaventoryWriteQueue.isQueuedOrSaving(
    purchaseOrderMegaventoryChangeKey({ type: "amount_rounding_mode", amount_rounding_mode: "line" }),
  );
  const specialInstructionsSavePending = megaventoryWriteQueue.isQueuedOrSaving(
    purchaseOrderMegaventoryChangeKey({ type: "special_instructions", special_instructions: null }),
  );
  const documentHeaderDirty =
    isHubOnlyDraft &&
    (trimText(hubDraftLocationCode) !== trimText(purchaseOrder.location_code) ||
      parsePositiveSupplierId(hubDraftSupplierId) !==
        parsePositiveSupplierId(purchaseOrder.supplier_id));
  const documentPendingNewLinesDirty = pendingNewLines.some(
    (row) =>
      trimText(row.sku) !== "" ||
      trimText(row.quantity) !== "" ||
      trimText(row.unit_price) !== "" ||
      trimText(row.line_description) !== "" ||
      trimText(row.expected_delivery_date) !== "",
  );
  const documentIsDirty = documentHeaderDirty || documentPendingNewLinesDirty;
  const documentSavePending =
    draftAutosaveStatus === "saving" ||
    megaventoryWriteQueue.queueDepth > 0 ||
    hubSupplierSavePending ||
    hubLocationSavePending;
  const documentSaveSucceeded =
    draftAutosaveStatus === "saved" ||
    hubSupplierJustSaved ||
    hubLocationJustSaved ||
    paymentTermsJustSaved ||
    amountRoundingModeJustSaved ||
    specialInstructionsJustSaved;
  const documentSaveError =
    hubSupplierSaveError ??
    hubLocationSaveError ??
    (draftAutosaveStatus === "error" ? "Could not save this purchase order." : null);
  const lifecyclePending =
    verifyMutation.isPending ||
    revertMutation.isPending ||
    cancelMutation.isPending ||
    closeMutation.isPending;
  const printableLineCount = isDraftOwner ? draftLines.length : lines.length;
  const canDownloadPurchaseOrderPdf = Number.isFinite(id) && printableLineCount > 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <HubPageTitle breadcrumbLabel={pageTitle}>{pageTitle}</HubPageTitle>
      </div>

      {isDraftOwner && draftErrorBanner ? (
        <p className="text-sm text-rose-700">{draftErrorBanner}</p>
      ) : null}
      {showLifecycleActions && verifyMutation.error ? (
        <p className="text-sm text-rose-700">{(verifyMutation.error as Error).message}</p>
      ) : null}
      {showLifecycleActions && revertMutation.error ? (
        <p className="text-sm text-rose-700">{(revertMutation.error as Error).message}</p>
      ) : null}
      {showLifecycleActions && cancelMutation.error ? (
        <p className="text-sm text-rose-700">{(cancelMutation.error as Error).message}</p>
      ) : null}
      {showLifecycleActions && closeMutation.error ? (
        <p className="text-sm text-rose-700">{(closeMutation.error as Error).message}</p>
      ) : null}
      {downloadPdfMutation.error ? (
        <p className="text-sm text-rose-700">{(downloadPdfMutation.error as Error).message}</p>
      ) : null}
      {showLifecycleActions && createVendorInvoiceMutation.error ? (
        <p className="text-sm text-rose-700">{(createVendorInvoiceMutation.error as Error).message}</p>
      ) : null}
      {showLifecycleActions && financialDocSuccess ? (
        <p className="text-sm text-green-700">
          GIPI created (MV document {String(financialDocSuccess.mv_document_id ?? "")}).
        </p>
      ) : null}

      <Card>
        <CardHeader className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <CardTitle className="font-mono tracking-tight">
              PO {purchaseOrder.po_number?.trim() || "----"}
            </CardTitle>
            {isHubOwned ? (
              <>
                <HoverHintContent hoverHint="Hub purchase order lifecycle status." cursor="default">
                  <Badge tone={hubPurchaseOrderStatusBadgeTone(hubStatus)}>
                    {hubPurchaseOrderStatusLabel(hubStatus)}
                  </Badge>
                </HoverHintContent>
                {purchaseOrder.fulfillment_status ? (
                  <HoverHintContent hoverHint={MRP_PURCHASE_ORDER_FULFILLMENT_HOVER_HINT} cursor="default">
                    <Badge tone="neutral">
                      {purchaseOrderFulfillmentStatusLabel(purchaseOrder.fulfillment_status)}
                    </Badge>
                  </HoverHintContent>
                ) : null}
              </>
            ) : (
              <>
                {!isHubOwned && purchaseOrder.purchase_order_mv_id != null ? (
                  <MegaventoryPurchaseOrderExternalLink
                    purchaseOrderMvId={purchaseOrder.purchase_order_mv_id}
                    poNumber={purchaseOrder.po_number}
                  />
                ) : null}
                <HoverHintContent hoverHint={MRP_PURCHASE_ORDER_MV_STATUS_HOVER_HINT} cursor="default">
                  <Badge tone={purchaseOrderMvStatusBadgeTone(purchaseOrder.mv_status)}>
                    {mrpPurchaseOrderMvStatusLabel(purchaseOrder.mv_status)}
                  </Badge>
                </HoverHintContent>
              </>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <DeferredCommitStatusGlyph
              pending={documentSavePending}
              isDirty={documentIsDirty}
              disabled={!hubWritesAllowed}
              commitError={documentSaveError}
              commitSucceeded={documentSaveSucceeded}
              showSavedWhenIdle={true}
              savedHint="All changes saved"
            />
            {canDownloadPurchaseOrderPdf ? (
              <Button
                type="button"
                variant="secondary"
                isLoading={downloadPdfMutation.isPending}
                disabled={documentIsDirty || documentSavePending}
                title={
                  documentIsDirty || documentSavePending
                    ? "Save the purchase order before you download."
                    : undefined
                }
                onClick={() => downloadPdfMutation.mutate()}
              >
                Download PDF
              </Button>
            ) : null}
            {isDraftOwner ? (
              <>
                <ReadOnlyHoverLayer
                  active={!hubWritesAllowed}
                  hoverHint="Your role has read-only access to the MRP hub module."
                  layout="inline"
                >
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={
                      !hubWritesAllowed ||
                      deleteDraftMutation.isPending ||
                      createInMegaventoryMutation.isPending
                    }
                    isLoading={deleteDraftMutation.isPending}
                    onClick={() => {
                      deleteDraftMutation.mutate();
                    }}
                  >
                    Delete draft
                  </Button>
                </ReadOnlyHoverLayer>
                <ReadOnlyHoverLayer
                  active={!hubWritesAllowed}
                  hoverHint="Your role has read-only access to the MRP hub module."
                  layout="inline"
                >
                  <Button
                    type="button"
                    disabled={
                      !hubWritesAllowed ||
                      deleteDraftMutation.isPending ||
                      createInMegaventoryMutation.isPending
                    }
                    isLoading={createInMegaventoryMutation.isPending}
                    onClick={() => {
                      if (showFinalize || showCreateInMegaventory) {
                        createInMegaventoryMutation.mutate(undefined);
                        return;
                      }
                      const body = submitDraftBody();
                      if (body) createInMegaventoryMutation.mutate(body);
                    }}
                  >
                    {showFinalize ? "Finalize" : "Approve"}
                  </Button>
                </ReadOnlyHoverLayer>
              </>
            ) : null}
            {showLifecycleActions ? (
              <>
                {canApprove ? (
                  <ReadOnlyHoverLayer
                    active={!hubWritesAllowed}
                    hoverHint="Your role has read-only access to the MRP hub module."
                    layout="inline"
                  >
                    <Button
                      type="button"
                      disabled={!hubWritesAllowed || lifecyclePending || (isHubOwned && !hasLinkedSupplier)}
                      isLoading={verifyMutation.isPending}
                      onClick={() => verifyMutation.mutate()}
                    >
                      {isHubOwned ? "Order" : "Approve"}
                    </Button>
                  </ReadOnlyHoverLayer>
                ) : null}
                {canApprove && apiOrderingProvider === "digikey" ? (
                  <ReadOnlyHoverLayer
                    active={!hubWritesAllowed}
                    hoverHint="Your role has read-only access to the MRP hub module."
                    layout="inline"
                  >
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={!hubWritesAllowed || lifecyclePending || !hasLinkedSupplier}
                      onClick={() => setDigikeyOrderOpen(true)}
                    >
                      Order via API
                    </Button>
                  </ReadOnlyHoverLayer>
                ) : null}
                {canRevertToPending ? (
                  <ReadOnlyHoverLayer
                    active={!hubWritesAllowed}
                    hoverHint="Your role has read-only access to the MRP hub module."
                    layout="inline"
                  >
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={!hubWritesAllowed || lifecyclePending}
                      isLoading={revertMutation.isPending}
                      onClick={() => revertMutation.mutate()}
                    >
                      {isHubOwned ? "Revert to draft" : "Revert to pending"}
                    </Button>
                  </ReadOnlyHoverLayer>
                ) : null}
                {canCancel ? (
                  <ReadOnlyHoverLayer
                    active={!hubWritesAllowed}
                    hoverHint="Your role has read-only access to the MRP hub module."
                    layout="inline"
                  >
                    <Button
                      type="button"
                      variant="danger"
                      disabled={!hubWritesAllowed || lifecyclePending}
                      isLoading={cancelMutation.isPending}
                      onClick={() => cancelMutation.mutate()}
                    >
                      Cancel
                    </Button>
                  </ReadOnlyHoverLayer>
                ) : null}
                {canClose ? (
                  <ReadOnlyHoverLayer
                    active={!hubWritesAllowed}
                    hoverHint="Your role has read-only access to the MRP hub module."
                    layout="inline"
                  >
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={!hubWritesAllowed || lifecyclePending}
                      isLoading={closeMutation.isPending}
                      onClick={() => {
                        const reason = window.prompt("Reason for closing this purchase order:");
                        if (reason == null) {
                          return;
                        }
                        if (reason.trim() === "") {
                          window.alert("A reason is required to close a purchase order.");
                          return;
                        }
                        closeMutation.mutate(reason.trim());
                      }}
                    >
                      Close
                    </Button>
                  </ReadOnlyHoverLayer>
                ) : null}
                {isHubOwned && !purchaseOrderCancelled ? (
                  <>
                    {draftApInvoice != null ? (
                      <Link
                        to={`/accounting/ap/${encodeURIComponent(draftApInvoice.id)}/edit`}
                        className="inline-flex h-10 items-center justify-center rounded-md border border-slate-300 bg-white px-4 text-sm font-medium text-slate-900 hover:bg-slate-100"
                      >
                        Edit vendor invoice draft
                      </Link>
                    ) : (
                      <ReadOnlyHoverLayer
                        active={!hubWritesAllowed}
                        hoverHint="Your role has read-only access to the MRP hub module."
                        layout="inline"
                      >
                        <Button
                          type="button"
                          variant="secondary"
                          disabled={!hubWritesAllowed || createVendorInvoiceMutation.isPending}
                          isLoading={createVendorInvoiceMutation.isPending}
                          onClick={() => createVendorInvoiceMutation.mutate()}
                        >
                          Create vendor invoice
                        </Button>
                      </ReadOnlyHoverLayer>
                    )}
                    <ReadOnlyHoverLayer
                      active={!hubWritesAllowed}
                      hoverHint="Your role has read-only access to the MRP hub module."
                      layout="inline"
                    >
                      <Button
                        type="button"
                        disabled={!hubWritesAllowed}
                        onClick={() => {
                          setFinancialDocSuccess(null);
                          setFinancialDocDialogOpen(true);
                        }}
                      >
                        Create GIPI
                      </Button>
                    </ReadOnlyHoverLayer>
                  </>
                ) : null}
                {!isHubOwned && !purchaseOrderCancelled ? (
                  <ReadOnlyHoverLayer
                    active={!hubWritesAllowed}
                    hoverHint="Your role has read-only access to the MRP hub module."
                    layout="inline"
                  >
                    <Button
                      type="button"
                      disabled={!hubWritesAllowed}
                      onClick={() => {
                        setFinancialDocSuccess(null);
                        setFinancialDocDialogOpen(true);
                      }}
                    >
                      Create GIPI in Megaventory
                    </Button>
                  </ReadOnlyHoverLayer>
                ) : null}
              </>
            ) : null}
          </div>
        </CardHeader>
        <CardBody>
          <div className="grid gap-3 md:grid-cols-3">
            <div className="min-w-0">
              <PurchaseOrderHeaderFieldLabel
                label="Supplier"
                hoverHint={
                  isHubOnlyDraft
                    ? "Megaventory supplier on this purchase order. Saved as soon as you pick one."
                    : undefined
                }
                trailing={
                  isHubOnlyDraft ? (
                    <DeferredCommitStatusGlyph
                      pending={hubSupplierSavePending}
                      isDirty={false}
                      disabled={!hubWritesAllowed}
                      commitError={hubSupplierSaveError}
                      commitSucceeded={hubSupplierJustSaved}
                    />
                  ) : null
                }
              />
              {isHubOnlyDraft ? (
                <ReadOnlyHoverLayer
                  active={!hubWritesAllowed}
                  hoverHint="Your role has read-only access to the MRP hub module."
                  layout="block"
                >
                  <SupplierSearchableSelect
                    value={hubDraftSupplierId}
                    disabled={!hubWritesAllowed}
                    extraRows={hubDraftSupplierQuery.data ? [hubDraftSupplierQuery.data] : []}
                    onValueChange={(option) => {
                      if (!option) {
                        setHubDraftSupplierId("");
                        setHubDraftSupplierLabel(null);
                        prevHubDraftSupplierIdRef.current = "";
                        lastAutosavePayloadRef.current = null;
                        void enqueueHubDraftPersist({
                          locationCode: hubDraftLocationCode,
                          supplierId: null,
                          statusFields: ["supplier"],
                        }).catch(() => undefined);
                        return;
                      }
                      const selectedSupplierId = parsePositiveSupplierId(option.value);
                      setHubDraftSupplierId(option.value);
                      setHubDraftSupplierLabel(option.label);
                      setHubDraftSupplierSearch("");
                      prevHubDraftSupplierIdRef.current = "";
                      lastAutosavePayloadRef.current = null;
                      void enqueueHubDraftPersist({
                        locationCode: hubDraftLocationCode,
                        supplierId: selectedSupplierId,
                        statusFields: ["supplier"],
                      }).catch(() => undefined);
                    }}
                    searchValue={hubDraftSupplierSearch}
                    onSearchChange={setHubDraftSupplierSearch}
                    placeholder="Select supplier…"
                    searchPlaceholder="Search by name or id…"
                    emptyMessage="No suppliers match."
                    selectedLabelOverride={hubDraftSupplierLabel}
                    className={cn(
                      hubSupplierSavePending && "opacity-70",
                      deferredCommitFlashClassName(hubSupplierFlashTone),
                    )}
                  />
                </ReadOnlyHoverLayer>
              ) : (
                <Input value={purchaseOrder.supplier ?? ""} readOnly className="bg-slate-50" />
              )}
            </div>
            <div className="min-w-0">
              <PurchaseOrderHeaderFieldLabel
                label="Location"
                hoverHint={
                  isHubOnlyDraft
                    ? "Ship-to Megaventory location for this purchase order. Saved as soon as you change it."
                    : undefined
                }
                trailing={
                  isHubOnlyDraft ? (
                    <DeferredCommitStatusGlyph
                      pending={hubLocationSavePending}
                      isDirty={false}
                      disabled={!hubWritesAllowed}
                      commitError={hubLocationSaveError}
                      commitSucceeded={hubLocationJustSaved}
                    />
                  ) : null
                }
              />
              {isHubOnlyDraft ? (
                <ReadOnlyHoverLayer
                  active={!hubWritesAllowed}
                  hoverHint="Your role has read-only access to the MRP hub module."
                  layout="block"
                >
                  <select
                    value={hubDraftLocationId != null ? String(hubDraftLocationId) : ""}
                    disabled={!hubWritesAllowed}
                    onChange={(event) => {
                      const nextLocationId = Number.parseInt(event.target.value, 10);
                      const nextOption = hubDraftLocationSelectOptions.find(
                        (option) => option.locationId === nextLocationId,
                      );
                      const nextLocation = nextOption?.value ?? hubDraftLocationCode;
                      setHubDraftLocationId(Number.isFinite(nextLocationId) ? nextLocationId : null);
                      setHubDraftLocationCode(nextLocation);
                      updateDraftLines((currentLines) =>
                        currentLines.map((line) => ({
                          ...line,
                          transfer_origin_location_id: String(
                            Number.isFinite(nextLocationId)
                              ? nextLocationId
                              : defaultPurchaseOrderTransferOriginPublicLocationId(nextLocation),
                          ),
                          transfer_destination_location_id: String(
                            defaultPurchaseOrderTransferDestinationPublicLocationId(nextLocation),
                          ),
                        })),
                      );
                      lastAutosavePayloadRef.current = null;
                      void enqueueHubDraftPersist({
                        locationCode: nextLocation,
                        locationId: Number.isFinite(nextLocationId) ? nextLocationId : null,
                        supplierId: parsePositiveSupplierId(hubDraftSupplierId),
                        statusFields: ["location"],
                      }).catch(() => undefined);
                    }}
                    className={cn(
                      "h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500",
                      hubLocationSavePending && "opacity-70",
                      deferredCommitFlashClassName(hubLocationFlashTone),
                    )}
                  >
                    {hubDraftLocationSelectOptions.map((location) =>
                      location.kind === "divider" ? (
                        <option key={location.value} disabled value={location.value}>------------------------</option>
                      ) : (
                        <option key={location.locationId} value={String(location.locationId)}>
                          {location.label}
                        </option>
                      ),
                    )}
                  </select>
                </ReadOnlyHoverLayer>
              ) : (
                <Input
                  value={
                    hubDraftLocationOptions.find((option) => option.locationId === purchaseOrder.location_id)
                      ?.label ??
                    hubDraftLocationOptions.find(
                      (option) => option.value === purchaseOrder.location_code.trim().toLowerCase(),
                    )?.label ??
                    purchaseOrder.location_code
                  }
                  readOnly
                  className="bg-slate-50"
                />
              )}
            </div>
            <div className="min-w-0">
              <PurchaseOrderHeaderFieldLabel
                label="Payment terms"
                hoverHint="Payment terms on this purchase order. Prefilled from the supplier when the order is created; you can change them here even after approval. Amber means this order differs from the supplier default."
                trailing={
                  isDraftOwner ? null : (
                    <DeferredCommitStatusGlyph
                      pending={paymentTermsSavePending}
                      isDirty={false}
                      disabled={!canEditHeaderFields}
                      commitError={paymentTermsSaveError}
                      commitSucceeded={paymentTermsJustSaved}
                    />
                  )
                }
              />
              <ReadOnlyHoverLayer
                active={!canEditHeaderFields}
                hoverHint={
                  !hubWritesAllowed
                    ? "Your role has read-only access to the MRP hub module."
                    : "Cancelled or closed purchase orders cannot be edited."
                }
                layout="block"
              >
                <select
                  value={isDraftOwner ? draftPaymentTerms : (purchaseOrder.payment_terms ?? "None")}
                  disabled={!canEditHeaderFields}
                  onChange={(event) => {
                    const paymentTermsForMegaventory = megaventoryEnumerationFromPaymentTermsUserInput(
                      event.target.value,
                      enumerationToLabel,
                    );
                    if (isDraftOwner) {
                      setDraftPaymentTerms(paymentTermsForMegaventory);
                      scheduleDraftAutosaveAfterSelectChange();
                      return;
                    }
                    void enqueuePaymentTermsChange(paymentTermsForMegaventory);
                  }}
                  onBlur={isDraftOwner ? handleDraftFieldBlur : undefined}
                  className={cn(
                    "h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500",
                    (isDraftOwner ? paymentTermsDiffersFromSupplierDraft : paymentTermsDiffersFromSupplier) &&
                      "border-amber-400 bg-amber-50 focus:border-amber-500 focus:ring-amber-500",
                    !isDraftOwner && paymentTermsSavePending && "opacity-70",
                    !isDraftOwner && deferredCommitFlashClassName(paymentTermsFlashTone),
                  )}
                >
                  {paymentTermsSelectRows.map((row) => (
                    <option key={row.mvEnumeration} value={row.mvEnumeration}>
                      {row.displayLabel}
                    </option>
                  ))}
                </select>
              </ReadOnlyHoverLayer>
              {(isDraftOwner ? paymentTermsDiffersFromSupplierDraft : paymentTermsDiffersFromSupplier) &&
              purchaseOrder.supplier_payment_terms ? (
                <p className="mt-1 text-xs font-medium text-amber-800">
                  Differs from supplier (
                  {mapMegaventoryPaymentTermsToLabel(
                    purchaseOrder.supplier_payment_terms,
                    enumerationToLabel,
                  ) ?? purchaseOrder.supplier_payment_terms}
                  )
                </p>
              ) : null}
            </div>
            <div className="min-w-0">
              <PurchaseOrderHeaderFieldLabel
                label="Amount rounding"
                hoverHint={PURCHASE_ORDER_AMOUNT_ROUNDING_HOVER_HINT}
                trailing={
                  <DeferredCommitStatusGlyph
                    pending={amountRoundingModeSavePending}
                    isDirty={false}
                    disabled={!canEditHeaderFields}
                    commitError={amountRoundingModeSaveError}
                    commitSucceeded={amountRoundingModeJustSaved}
                  />
                }
              />
              <ReadOnlyHoverLayer
                active={!canEditHeaderFields}
                hoverHint={
                  !hubWritesAllowed
                    ? "Your role has read-only access to the MRP hub module."
                    : "Cancelled or closed purchase orders cannot be edited."
                }
                layout="block"
              >
                <select
                  value={parsePurchaseOrderAmountRoundingMode(purchaseOrder.amount_rounding_mode)}
                  disabled={!canEditHeaderFields}
                  onChange={(event) => {
                    void enqueueAmountRoundingModeChange(event.target.value);
                  }}
                  className={cn(
                    "h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500",
                    amountRoundingModeSavePending && "opacity-70",
                    deferredCommitFlashClassName(amountRoundingModeFlashTone),
                  )}
                >
                  <option value="line">{PURCHASE_ORDER_AMOUNT_ROUNDING_MODE_LABELS.line}</option>
                  <option value="order">{PURCHASE_ORDER_AMOUNT_ROUNDING_MODE_LABELS.order}</option>
                </select>
              </ReadOnlyHoverLayer>
            </div>
            <div className="min-w-0">
              <PurchaseOrderHeaderFieldLabel label="Created in hub" />
              <Input
                value={
                  hubCreatedInHubLabel ??
                  (timeline?.hub_created_at
                    ? `${formatPurchaseOrderTimestamp(timeline.hub_created_at)} · ${formatPoActor(timeline.hub_created_by)}`
                    : "—")
                }
                readOnly
                className="bg-slate-50"
              />
            </div>
            <div className="min-w-0">
              <PurchaseOrderHeaderFieldLabel label="Approved in hub" />
              <Input
                value={
                  timeline?.hub_verified_at
                    ? `${formatPurchaseOrderTimestamp(timeline.hub_verified_at)} · ${formatPoActor(timeline.hub_verified_by)}`
                    : "—"
                }
                readOnly
                className="bg-slate-50"
              />
            </div>
            {!isHubOwned ? (
              <div className="min-w-0">
                <PurchaseOrderHeaderFieldLabel label="Date created (Megaventory)" />
                <Input
                  value={formatPurchaseOrderTimestamp(timeline?.mv_created_at ?? purchaseOrder.mv_created_at)}
                  readOnly
                  className="bg-slate-50"
                />
              </div>
            ) : null}
            <div className="min-w-0">
              {isDraftOwner ? (
                <>
                  <PurchaseOrderHeaderFieldLabel
                    label="Special instructions"
                    hoverHint="Megaventory purchase order comments on the order header. Autosaved with the rest of the draft."
                  />
                  <ReadOnlyHoverLayer
                    active={!canEditHeaderFields}
                    hoverHint={
                      !hubWritesAllowed
                        ? "Your role has read-only access to the MRP hub module."
                        : "Cancelled or closed purchase orders cannot be edited."
                    }
                    layout="block"
                  >
                    <TextArea
                      value={draftComments}
                      disabled={!canEditHeaderFields}
                      onChange={(event) => setDraftComments(event.target.value)}
                      onBlur={handleDraftFieldBlur}
                      className="min-h-10 resize-y font-sans text-sm"
                      placeholder="Notes for the supplier on this purchase order…"
                    />
                  </ReadOnlyHoverLayer>
                </>
              ) : (
                <ReadOnlyHoverLayer
                  active={!canEditHeaderFields}
                  hoverHint={
                    !hubWritesAllowed
                      ? "Your role has read-only access to the MRP hub module."
                      : "Cancelled or closed purchase orders cannot be edited."
                  }
                  layout="block"
                >
                  <DeferredCommitTextArea
                    label={
                      <PurchaseOrderHeaderFieldLabel
                        embedded
                        label="Special instructions"
                        hoverHint="Comments on the order header. Editable after approval without changing approval status."
                      />
                    }
                    value={purchaseOrder.special_instructions ?? ""}
                    disabled={!canEditHeaderFields}
                    pending={specialInstructionsSavePending}
                    commitSucceeded={specialInstructionsJustSaved}
                    commitError={specialInstructionsSaveError}
                    onCommit={(special_instructions) => {
                      void enqueueSpecialInstructionsChange(special_instructions);
                    }}
                    className="min-h-10 resize-y font-sans text-sm"
                    placeholder="Notes for the supplier on this purchase order…"
                  />
                </ReadOnlyHoverLayer>
              )}
            </div>
            {isHubOwned ? (
              <div className="md:col-span-2">
                <Label htmlFor="po-freshdesk-tickets">Freshdesk tickets</Label>
                <p className="mb-2 text-xs text-slate-500">Internal links only — not sent to the supplier.</p>
                <QualityFreshdeskTicketPicker
                  id="po-freshdesk-tickets"
                  values={poQuery.data?.freshdesk_ticket_ids ?? []}
                  disabled={!hubWritesAllowed}
                  onChange={async (ticketIds) => {
                    const current = new Set(poQuery.data?.freshdesk_ticket_ids ?? []);
                    const next = new Set(ticketIds);
                    const added = ticketIds.filter((ticketId) => !current.has(ticketId));
                    const removed = (poQuery.data?.freshdesk_ticket_ids ?? []).filter(
                      (ticketId) => !next.has(ticketId),
                    );
                    let freshdeskTicketIds = poQuery.data?.freshdesk_ticket_ids ?? [];
                    for (const ticketId of added) {
                      freshdeskTicketIds = await addPurchaseOrderFreshdeskTicket(id, ticketId);
                    }
                    for (const ticketId of removed) {
                      freshdeskTicketIds = await removePurchaseOrderFreshdeskTicket(id, ticketId);
                    }
                    queryClient.setQueryData(["mrp", "purchase-orders", "one", id], (current) =>
                      current != null ? { ...current, freshdesk_ticket_ids: freshdeskTicketIds } : current,
                    );
                  }}
                />
              </div>
            ) : null}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitleWithHelp hoverHint="Purchase order lines with expected dates, descriptions, and optional transfer requests.">
            Lines
          </CardTitleWithHelp>
          <div className="shrink-0 text-right">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Order total</p>
            <p className="font-mono text-sm font-semibold tabular-nums text-slate-900">
              {formatPoMoney(displayOrderTotal, displayCurrency)}
            </p>
          </div>
        </CardHeader>
        <CardBody className="overflow-x-auto p-0">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2">CPN</th>
                <th className="min-w-[14rem] px-3 py-2">
                  <span className="inline-flex items-center gap-0.5">
                    Description
                    <HoverHintWithGlyph hoverHint="Megaventory copies SKU description and variant onto the purchase order line when the line is added. That text is stored on the line so it can be edited and can differ if the SKU catalog changes later. Amber highlight means the line no longer matches the current SKU." />
                  </span>
                </th>
                <th className="w-[4.5rem] max-w-[4.5rem] px-2 py-2">UOM</th>
                <th className="w-[5.5rem] max-w-[5.5rem] px-2 py-2 text-right">Quantity</th>
                <th className="w-[5.5rem] max-w-[5.5rem] px-2 py-2 text-right">Unit price</th>
                <th className="px-3 py-2 text-right">Line total</th>
                <th className="px-3 py-2 text-right">
                  <span className="inline-flex items-center justify-end gap-0.5">
                    Received
                    <HoverHintWithGlyph hoverHint={MRP_PURCHASE_ORDER_RECEIVED_QTY_HOVER_HINT} />
                  </span>
                </th>
                <th className="px-3 py-2 text-right">
                  <span className="inline-flex items-center justify-end gap-0.5">
                    Invoiced
                    <HoverHintWithGlyph hoverHint={MRP_PURCHASE_ORDER_INVOICED_QTY_HOVER_HINT} />
                  </span>
                </th>
                <th className="px-3 py-2 text-right">
                  <span className="inline-flex items-center justify-end gap-0.5">
                    GIPI
                    <HoverHintWithGlyph hoverHint={MRP_PURCHASE_ORDER_GIPI_QTY_HOVER_HINT} />
                  </span>
                </th>
                <th className="px-3 py-2">Expected date</th>
                <th className="min-w-[11rem] px-2 py-2" aria-label="Remove line" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <PurchaseOrderGroupedDraftLines
                lines={isDraftOwner ? draftLines : savedLinesWithPendingAdds}
                onLinesChange={
                  isDraftOwner
                    ? updateDraftLines
                    : (next) => {
                        setPendingNewLines(next.filter((line) => line.purchase_order_item_id == null));
                      }
                }
                locationCode={isHubOnlyDraft ? hubDraftLocationCode : purchaseOrder.location_code}
                currency={isDraftOwner ? displayCurrency : purchaseOrder.currency}
                amountRoundingMode={parsePurchaseOrderAmountRoundingMode(purchaseOrder.amount_rounding_mode)}
                disabled={isDraftOwner ? createInMegaventoryMutation.isPending : false}
                onFieldBlur={isDraftOwner ? handleDraftFieldBlur : undefined}
                hubWritesAllowed={hubWritesAllowed}
                skuHoverHints={
                  (isDraftOwner ? draftSkuHintsQuery : skuHintsQuery).data?.sku_hover_hints
                }
                mode={isDraftOwner ? "draft" : "saved"}
                savedLineHandlers={savedLineHandlers}
              />
              {!isDraftOwner && savedLinesWithPendingAdds.length === 0 ? (
                <tr>
                  <td className="px-4 py-6 text-center text-slate-500" colSpan={10}>
                    No lines on this purchase order.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          {isDraftOwner || savedLineHandlers?.canAddRemoveLines === true ? (
            <div className="border-t border-slate-100 px-3 py-3">
              <ReadOnlyHoverLayer
                active={!hubWritesAllowed}
                hoverHint="Your role has read-only access to the MRP hub module."
                layout="inline"
              >
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  disabled={
                    !hubWritesAllowed || createInMegaventoryMutation.isPending
                  }
                  onClick={() => {
                    const nextLine = emptyMvLine(
                      isHubOnlyDraft ? hubDraftLocationCode : purchaseOrder.location_code,
                    );
                    if (isDraftOwner) {
                      updateDraftLines([...draftLines, nextLine]);
                      return;
                    }
                    setPendingNewLines((previous) => [...previous, { ...nextLine, quantity: "1" }]);
                  }}
                >
                  Add line
                </Button>
              </ReadOnlyHoverLayer>
            </div>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitleWithHelp
            hoverHint="Open planner transfer demand linked to this purchase order. Use Transfer request on a line to add one (same fields as Create purchase order). Open requests appear on the Transfers workbench."
          >
            Linked transfer requests
          </CardTitleWithHelp>
        </CardHeader>
        <CardBody>
          {transferRequests.length === 0 ? (
            <p className="text-sm text-slate-600">
              None linked yet. Use{" "}
              <span className="font-mono font-medium text-slate-800">T</span> beside a SKU above to create open
              planner demand for that stock keeping unit.
            </p>
          ) : (
            <>
              <p className="mb-3 text-sm text-slate-600">
                Open requests appear on the{" "}
                <Link
                  to="/manufacturing-management/transfers"
                  className="font-medium text-brand-700 hover:underline"
                >
                  Transfers workbench
                </Link>
                .
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                      <th className="px-2 py-2">SKU</th>
                      <th className="px-2 py-2">Route</th>
                      <th className="px-2 py-2">Quantity</th>
                      <th className="px-2 py-2">Target date</th>
                      <th className="px-2 py-2">Status</th>
                      <th className="px-2 py-2">Reason</th>
                      <th className="px-2 py-2">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transferRequests.map((row) => (
                      <tr key={row.id} className="border-b border-slate-100 last:border-b-0">
                        <td className="px-2 py-2 font-mono text-xs">{row.sku_id}</td>
                        <td className="px-2 py-2">
                          {transferEndpointLocationLabelById(transferLocations, row.origin_location_id)} →{" "}
                          {transferEndpointLocationLabelById(transferLocations, row.destination_location_id)}
                        </td>
                        <td className="px-2 py-2 tabular-nums">
                          {formatNumber(row.remaining_quantity)} / {formatNumber(row.requested_quantity)}
                        </td>
                        <td className="px-2 py-2">{row.target_date}</td>
                        <td className="px-2 py-2 capitalize">{row.status.replaceAll("_", " ")}</td>
                        <td className="px-2 py-2">{mrpTransferReasonLabel(row.reason)}</td>
                        <td className="px-2 py-2 text-slate-600">{row.notes?.trim() || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardBody>
      </Card>

      {isHubOwned ? (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-3">
            <CardTitleWithHelp hoverHint="Warehouse Goods IN for this purchase order. Parentless GIPIs are not listed here; they still count in Received on the lines.">
              Receipt history
            </CardTitleWithHelp>
            <Link
              to={buildShippingInboundReceivePath("purchase_order", String(id))}
              className="inline-flex h-8 items-center justify-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-900 hover:bg-slate-100"
            >
              Open receive workspace
            </Link>
          </CardHeader>
          <CardBody>
            {inboundReceipts.length === 0 ? (
              <p className="text-sm text-slate-600">No inbound receipts recorded yet for this purchase order.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                      <th className="px-2 py-2">Received</th>
                      <th className="px-2 py-2">Status</th>
                      <th className="px-2 py-2 text-right">Quantity</th>
                      <th className="px-2 py-2">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inboundReceipts.map((receipt) => (
                      <tr key={receipt.id} className="border-b border-slate-100 last:border-b-0">
                        <td className="px-2 py-2 whitespace-nowrap tabular-nums">
                          {formatPurchaseOrderTimestamp(receipt.received_at)}
                        </td>
                        <td className="px-2 py-2 capitalize">{receipt.status.replaceAll("_", " ")}</td>
                        <td className="px-2 py-2 text-right tabular-nums">{formatNumber(receipt.received_quantity)}</td>
                        <td className="px-2 py-2 whitespace-nowrap tabular-nums">
                          {formatPurchaseOrderTimestamp(receipt.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      ) : null}

      {isHubOwned && directGipis.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitleWithHelp hoverHint="Parentless Megaventory GIPIs created from this hub purchase order via Create GIPI. These count in Received and GIPI. GIPIs created from a vendor invoice appear under Vendor invoices.">
              Direct GIPIs
            </CardTitleWithHelp>
          </CardHeader>
          <CardBody className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <th className="px-2 py-2">GIPI</th>
                  <th className="px-2 py-2">Vendor invoice</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2">Created</th>
                </tr>
              </thead>
              <tbody>
                {directGipis.map((gipi) => (
                  <tr key={gipi.id} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-2 py-2">
                      <PurchaseInvoiceDetailLink
                        purchaseInvoiceId={Number.parseInt(gipi.id, 10)}
                        mvNumber={gipi.mv_number}
                        vendorInvoiceNumber={gipi.vendor_invoice_number}
                      />
                    </td>
                    <td className="px-2 py-2">{gipi.vendor_invoice_number?.trim() || "—"}</td>
                    <td className="px-2 py-2">{gipi.mv_status?.trim() || "—"}</td>
                    <td className="px-2 py-2 whitespace-nowrap tabular-nums">
                      {gipi.created_at == null ? "—" : formatPurchaseOrderTimestamp(gipi.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      ) : null}

      {isHubOwned && accountsPayableInvoices.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitleWithHelp hoverHint="Accounts payable vendor invoices linked to this hub purchase order, including GIPI sync status after submit.">
              Vendor invoices
            </CardTitleWithHelp>
          </CardHeader>
          <CardBody className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                  <th className="px-2 py-2">Invoice</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2">GIPI sync</th>
                  <th className="px-2 py-2">GIPI</th>
                  <th className="px-2 py-2 text-right">Total</th>
                  <th className="px-2 py-2">Created</th>
                </tr>
              </thead>
              <tbody>
                {accountsPayableInvoices.map((invoice) => (
                  <tr key={invoice.id} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-2 py-2">
                      <Link
                        to={`/accounting/ap/${encodeURIComponent(invoice.id)}/edit`}
                        className="font-medium text-brand-700 hover:underline"
                      >
                        {invoice.vendor_invoice_number?.trim() || `Invoice #${invoice.id}`}
                      </Link>
                    </td>
                    <td className="px-2 py-2 capitalize">{invoice.status.replaceAll("_", " ")}</td>
                    <td className="px-2 py-2">{invoice.gipi_sync_status?.replaceAll("_", " ") ?? "—"}</td>
                    <td className="px-2 py-2">
                      {invoice.purchase_invoice_id == null ? (
                        "—"
                      ) : (
                        <PurchaseInvoiceDetailLink
                          purchaseInvoiceId={Number.parseInt(invoice.purchase_invoice_id, 10)}
                          mvNumber={invoice.purchase_invoice_mv_number}
                        />
                      )}
                    </td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums">
                      {invoice.total_amount == null
                        ? "—"
                        : formatPoMoney(invoice.total_amount, purchaseOrder.currency)}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap tabular-nums">
                      {formatPurchaseOrderTimestamp(invoice.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>History</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="purchase-order-history-comment">Add comment</Label>
            <MentionTextarea
              id="purchase-order-history-comment"
              value={historyCommentDraft}
              disabled={!hubWritesAllowed || addHistoryCommentMutation.isPending}
              onChange={setHistoryCommentDraft}
              placeholder="Leave a note for others following this purchase order. Type @ to mention someone."
              className="min-h-[5rem] w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
            <div className="flex flex-wrap items-center gap-2">
              <ReadOnlyHoverLayer
                active={!hubWritesAllowed}
                hoverHint="Your role has read-only access to the MRP hub module."
                layout="inline"
              >
                <Button
                  type="button"
                  size="sm"
                  disabled={!hubWritesAllowed || historyCommentDraft.trim() === ""}
                  isLoading={addHistoryCommentMutation.isPending}
                  onClick={() => {
                    addHistoryCommentMutation.mutate(historyCommentDraft.trim());
                  }}
                >
                  Add comment
                </Button>
              </ReadOnlyHoverLayer>
              {addHistoryCommentMutation.error ? (
                <span className="text-sm text-rose-700">
                  {(addHistoryCommentMutation.error as Error).message}
                </span>
              ) : null}
            </div>
          </div>
          {events.length === 0 ? (
            <p className="text-sm text-slate-500">
              No hub activity recorded yet. Comments, approve/cancel actions, header edits, expected-date edits, and
              delivery splits from this app appear here.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-left text-slate-600">
                    <th className="py-2 pr-3 font-medium">When</th>
                    <th className="py-2 pr-3 font-medium">Source</th>
                    <th className="py-2 pr-3 font-medium">Type</th>
                    <th className="py-2 pr-3 font-medium">User</th>
                    <th className="py-2 font-medium">Summary</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event.id} className="border-b border-slate-100">
                      <td className="py-2 pr-3 whitespace-nowrap tabular-nums">{formatPurchaseOrderTimestamp(event.occurred_at)}</td>
                      <td className="py-2 pr-3 capitalize">{event.source === "sgs" ? "SGS" : "Megaventory sync"}</td>
                      <td className="py-2 pr-3">{PURCHASE_ORDER_EVENT_TYPE_LABEL[event.event_type] ?? event.event_type}</td>
                      <td className="py-2 pr-3">{formatPoActor(event.actor_email)}</td>
                      <td className="py-2">
                        <PurchaseOrderEventSummaryCell
                          event={event}
                          gipiMvNumber={gipiMvNumberByPurchaseInvoiceId.get(
                            Number(event.payload?.purchase_invoice_id),
                          )}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      {splitDialog ? (
        <PurchaseOrderSplitDeliveriesDialog
          purchaseOrderItemId={splitDialog.purchaseOrderItemId}
          cpn={splitDialog.cpn}
          prefillExpectedArrivalWhenNoSegments={splitDialog.prefillExpectedArrival}
          hubWritesAllowed={hubWritesAllowed}
          hubReadOnlyTitle="Your role has read-only access to the MRP hub module."
          onClose={() => setSplitDialog(null)}
          onSave={(segments) => {
            splitDeliveriesMutation.mutate({
              purchaseOrderItemId: splitDialog.purchaseOrderItemId,
              segments,
            });
          }}
          isSaving={splitDeliveriesMutation.isPending}
          saveError={splitDeliveriesMutation.error}
        />
      ) : null}
      {financialDocDialogOpen ? (
        <PurchaseOrderFinancialDocDialog
          purchaseOrderId={id}
          lines={lines}
          isHubOwned={isHubOwned}
          hubWritesAllowed={hubWritesAllowed}
          hubReadOnlyTitle="Your role has read-only access to the MRP hub module."
          onClose={() => setFinancialDocDialogOpen(false)}
          onCreated={async (result) => {
            setFinancialDocSuccess({
              mv_document_id: result.mv_document_id,
            });
            await invalidatePurchaseOrder();
          }}
        />
      ) : null}
      {digikeyOrderOpen ? (
        <DigiKeyOrderModal
          purchaseOrderId={id}
          onClose={() => setDigikeyOrderOpen(false)}
          onOrdered={() => void invalidatePurchaseOrder()}
        />
      ) : null}
    </div>
  );
}
